import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Form, Icon, Input, Label, Message, Modal, Popup, Progress, Table,} from 'semantic-ui-react';
import {Link} from 'react-router-dom';
import {
  API,
  loadChannelModels,
  setPromptShown,
  shouldShowPrompt,
  showError,
  showInfo,
  showSuccess,
  timestamp2string,
  isRoot,
} from '../helpers';
import ImportSiteModal from './ImportSiteModal';
import ModelChannelPriorityModal from './ModelChannelPriorityModal';
import ModelTierModal from './ModelTierModal';
import { StudioHero, StudioStat } from './StudioPage';
import {
  collectChannelModels,
  getChannelReportGroupNames,
  groupChannelsByName,
  mergeChannelReports,
  normalizeChannelGroupName,
} from './ChannelsTable.helpers';

import {CHANNEL_OPTIONS} from '../constants';
import {renderGroup, renderNumber} from '../helpers/render';

// 统一样式常量 —— 深空黑 + 紫蓝霓虹
const styles = {
  page: {
    background: 'transparent',
    borderRadius: 20,
    padding: 20,
  },
  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '18px 20px',
    marginBottom: 16,
    borderRadius: 18,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 55%, rgba(0,0,0,0.02) 100%)',
    border: '1px solid rgba(0,0,0,0.12)',
  },
  searchSection: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '14px 16px',
    marginBottom: 16,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 10,
    columnGap: 12,
    background: '#ffffff',
    borderRadius: 16,
    padding: '14px 16px',
    marginBottom: 16,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
  },
  tableWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'visible',
    overscrollBehaviorX: 'contain',
    WebkitOverflowScrolling: 'touch',
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.08)',
    background: '#ffffff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  },
  tableHeader: {
    background: '#f5f5f5',
    color: '#18181b',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap',
  },
  tableRow: {
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
    background: 'transparent',
  },
  tableRowHover: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.02) 100%)',
    boxShadow: 'inset 2px 0 0 #18181b, 0 8px 20px rgba(0,0,0,0.06)',
  },
  button: {
    borderRadius: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'transform 0.16s ease, box-shadow 0.16s ease',
  },
  groupRow: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.04) 100%)',
    cursor: 'pointer',
    transition: 'background 0.18s ease, transform 0.18s ease',
  },
  groupRowHover: {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.03) 100%)',
  },
};

/**
 * 渲染时间戳
 * @param {number} timestamp - 时间戳（秒）
 * @returns {JSX.Element}
 */
function renderTimestamp(timestamp) {
  if (!timestamp) return <span style={{ color: '#6b7280', fontSize: 11 }}>未测试</span>;
  return <span style={{ fontSize: 11, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{timestamp2string(timestamp)}</span>;
}

function renderLastSuccessTime(timestamp) {
  if (!timestamp) return <span style={{ color: '#9ca3af', fontSize: 11 }}>未调用</span>;
  return <span style={{ fontSize: 11, color: '#18181b', fontVariantNumeric: 'tabular-nums' }}>{timestamp2string(timestamp)}</span>;
}

/**
 * 渲染能力测试结果（文本 ✓ | 图片 ✓ | 工具 ✓）
 * @param {string} errorMsg - 报告中的 error_message 字段
 * @returns {JSX.Element}
 */
function renderCapabilityResult(errorMsg) {
  if (!errorMsg) return <span style={{ color: '#6b7280', fontSize: 11 }}>-</span>;
  // 后端格式: "文本 ✗ | 图片 ✗ | 工具 ✗ — 文本: xxx; 图片: xxx"
  const parts = errorMsg.split(' — ');
  const statusPart = parts[0] || errorMsg;
  const detailPart = parts.length > 1 ? parts[1] : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {statusPart.split(' | ').map((item, i) => {
          const passed = item.includes('✓');
          return (
            <span key={i} style={{
              padding: '2px 8px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: passed ? '#f0fdf4' : '#fef2f2',
              color: passed ? '#16a34a' : '#dc2626',
              border: `1px solid ${passed ? 'rgba(22,163,74,0.3)' : 'rgba(220,38,38,0.3)'}`,
            }}>
              {item}
            </span>
          );
        })}
      </div>
      {detailPart && (
        <span style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.4 }}>
          {detailPart}
        </span>
      )}
    </div>
  );
}

/**
 * 渠道类型标签缓存（按语言懒加载）
 */
let type2label = undefined;

/**
 * 渲染渠道类型标签
 * @param {number} type - 渠道类型
 * @param {Function} t - 翻译函数
 * @returns {JSX.Element}
 */
function renderType(type, t) {
  if (!type2label) {
    type2label = new Map();
    CHANNEL_OPTIONS.forEach((opt) => { type2label[opt.value] = opt; });
    type2label[0] = { value: 0, text: t('channel.table.status_unknown'), color: 'grey' };
  }
  const cfg = type2label[type];
  return <Label size='mini' basic color={cfg?.color || 'grey'}>{cfg ? cfg.text : type}</Label>;
}

/**
 * 余额类型配置
 */
const BALANCE_TYPE_CONFIG = {
  1: { currency: '$', divisor: 1, supported: true },
  4: { currency: '¥', divisor: 1, supported: true },
  5: { currency: '¥', divisor: 10000, supported: true },
  8: { currency: '$', divisor: 1, supported: true },
  10: { currency: '', divisor: 1, supported: true, render: renderNumber },
  12: { currency: '¥', divisor: 1, supported: true },
  13: { currency: '', divisor: 1, supported: true, render: renderNumber },
  20: { currency: '$', divisor: 1, supported: true },
  36: { currency: '¥', divisor: 1, supported: true },
  44: { currency: '¥', divisor: 1, supported: true },
};

/**
 * 渲染余额
 * @param {number} type - 渠道类型
 * @param {number} balance - 余额
 * @param {Function} t - 翻译函数
 * @returns {JSX.Element}
 */
function renderBalance(type, balance, t) {
  const cfg = BALANCE_TYPE_CONFIG[type];
  if (!cfg || !cfg.supported) {
    return <span style={{ color: '#6b7280', fontSize: 12 }}>{t('channel.table.balance_not_supported')}</span>;
  }
  if (balance === 0 && type === 1) {
    return <span style={{ color: '#6b7280', fontSize: 12 }}>{t('channel.table.balance_not_supported')}</span>;
  }
  const val = (balance / cfg.divisor).toFixed(2);
  const display = cfg.render ? cfg.render(balance) : `${cfg.currency}${val}`;
  return <span style={{ fontSize: 12, fontWeight: 600, color: '#18181b', fontVariantNumeric: 'tabular-nums' }}>{display}</span>;
}

/**
 * 渲染渠道模型列表（最多展示前 2 个 + "+N"）
 * @param {Array<string>} models - 模型名称列表
 * @param {Function} onClick - 点击事件（可选，用于打开模型管理）
 * @returns {JSX.Element}
 */
function renderModelTags(models, onClick) {
  if (!models || models.length === 0) {
    return (
      <button
        type='button'
        onClick={onClick}
        style={{
          color: '#6b7280',
          fontSize: 12,
          cursor: onClick ? 'pointer' : 'default',
          padding: '7px 10px',
          borderRadius: 999,
          border: onClick ? '1px dashed #cbd5e1' : '1px solid transparent',
          background: '#f5f5f5',
          lineHeight: 1,
        }}
      >
        {onClick ? '+ 添加模型' : '未设置模型'}
      </button>
    );
  }
  const max = 3;
  const show = models.slice(0, max);
  const rest = models.length - show.length;
  const tagStyle = {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: 999,
    background: 'rgba(0,0,0,0.04)',
    color: '#18181b',
    fontSize: 12,
    fontWeight: 600,
    border: '1px solid #bfdbfe',
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1,
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', maxWidth: 460 }}>
      {show.map((m) => <span key={m} style={tagStyle}>{m}</span>)}
      {rest > 0 && (
        <Popup
          content={
            <div style={{ maxWidth: 440, maxHeight: 320, overflow: 'auto' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {models.map((m) => <span key={m} style={tagStyle}>{m}</span>)}
              </div>
              {onClick && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
                  <Button size='mini' primary onClick={(e) => { e.stopPropagation(); onClick(); }} style={styles.button}>
                    <Icon name='edit' /> 管理模型
                  </Button>
                </div>
              )}
            </div>
          }
          trigger={<span style={{ ...tagStyle, background: '#f5f5f5', color: '#6b7280', borderColor: 'rgba(0,0,0,0.08)', cursor: 'help' }}>+{rest}</span>}
          hoverable
          wide
        />
      )}
      {onClick && (
        <button
          type='button'
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          style={{
            border: '1px solid #dbeafe',
            background: '#ffffff',
            color: '#18181b',
            borderRadius: 999,
            padding: '5px 9px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            lineHeight: 1,
            transition: 'background 0.16s ease, transform 0.16s ease',
          }}
        >
          管理
        </button>
      )}
    </div>
  );
}

const promptID = 'detail';

const ChannelsTable = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [updatingBalance, setUpdatingBalance] = useState(false);
  const [showPrompt, setShowPrompt] = useState(shouldShowPrompt(promptID));
  // 按渠道名称分组并支持展开/折叠
  // 折叠状态：key = "groupName" -> boolean (true 表示展开)
  const [expandedGroups, setExpandedGroups] = useState({});
  // testingChannels 记录正在测试中的渠道 ID，用于防抖和加载动效
  const [testingChannels, setTestingChannels] = useState(new Set());
  const testingChannelsRef = useRef(new Set());
  // 记录当前正在被测试的模型名（用于行内显示加载态）
  const [testingModelChannel, setTestingModelChannel] = useState(null); // { channelId, model }
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  // 模型管理弹窗状态
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelModalChannel, setModelModalChannel] = useState(null);
  const [modelModalModels, setModelModalModels] = useState('');
  const [modelModalLoading, setModelModalLoading] = useState(false);
  const [fetchModelsLoading, setFetchModelsLoading] = useState(false);
  // 分组批量管理弹窗状态
  const [groupModelModalOpen, setGroupModelModalOpen] = useState(false);
  const [groupModelModalGroup, setGroupModelModalGroup] = useState('');
  const [groupModelModalModels, setGroupModelModalModels] = useState('');
  const [groupModelModalAction, setGroupModelModalAction] = useState('add');
  const [groupModelModalLoading, setGroupModelModalLoading] = useState(false);
  // 导入站点弹窗状态
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [modelPriorityModalOpen, setModelPriorityModalOpen] = useState(false);
  const [modelTierModalOpen, setModelTierModalOpen] = useState(false);
  // 一键禁用/启用确认弹窗受控状态（存当前打开的分组名，空串表示关闭）
  const [batchConfirmGroup, setBatchConfirmGroup] = useState('');
  // 一键删除确认弹窗受控状态（与上面分开，避免开关互相干扰）
  const [batchDeleteConfirmGroup, setBatchDeleteConfirmGroup] = useState('');
  // 批量测试进度状态
  const [batchTestTask, setBatchTestTask] = useState(null); // { taskId, total, completed, success, failed, percentage, status, groupName }
  const batchTestTimerRef = useRef(null);
  // 测试报告弹窗状态
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportFilter, setReportFilter] = useState('all'); // all / success / failed
  const [reportTitle, setReportTitle] = useState('');
  const [reportSort, setReportSort] = useState({ field: '', direction: 'asc' });
  // 缓存各分组最近一批测试报告，用于在渠道行展示可用模型
  const [groupReports, setGroupReports] = useState({}); // { [groupName]: report[] }
  const loadedGroupReportsRef = useRef(new Set());
  // 全渠道模型探针状态
    const [modelProbeOpen, setModelProbeOpen] = useState(false);
    const [modelProbeModel, setModelProbeModel] = useState('');
    const [modelProbeConcurrency, setModelProbeConcurrency] = useState(5);
    const [modelProbeTask, setModelProbeTask] = useState(null);
    const [modelProbeReports, setModelProbeReports] = useState([]);
    const [modelProbeSort, setModelProbeSort] = useState({ field: 'response_time_ms', direction: 'asc' });
    const [modelProbeScope, setModelProbeScope] = useState('all');
    const modelProbeTimerRef = useRef(null);
    // 手机端检测
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
      const check = () => setIsMobile(window.innerWidth <= 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
      }, []);

      // 批量模型校准状态
      const [calibrateOpen, setCalibrateOpen] = useState(false);
      const [calibrateScope, setCalibrateScope] = useState('all');
      const [calibrateLoading, setCalibrateLoading] = useState(false);
      const [calibrateProgress, setCalibrateProgress] = useState(0); // 0-100
      const [calibrateProgressText, setCalibrateProgressText] = useState('');
      const [calibrateResult, setCalibrateResult] = useState(null);
      const [calibrateApplying, setCalibrateApplying] = useState(false);
      const [calibrateSelected, setCalibrateSelected] = useState(new Set());
      const [calibrateSearch, setCalibrateSearch] = useState('');

  /**
   * 处理渠道数据，解析模型列表
   * @param {Object} channel - 渠道对象
   * @returns {Object} 处理后的渠道对象
   */
  const processChannelData = useCallback((channel) => {
    if (channel.models === '' || !channel.models) {
      channel.models = [];
      channel.test_model = '';
    } else {
      channel.models = channel.models.split(',').filter(Boolean);
      if (channel.models.length > 0) {
        channel.test_model = channel.models[0];
      }
      channel.model_options = channel.models.map((model) => ({
        key: model,
        text: model,
        value: model,
      }));
    }
    return channel;
  }, []);

  /**
   * 加载全部渠道（不再分页）
   */
  const loadAllChannels = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/api/channel/?p=0&size=0');
      const { success, message, data } = res.data;
      if (success) {
        setChannels(data.map(processChannelData));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '加载渠道失败');
    } finally {
      setLoading(false);
    }
  }, [processChannelData]);

  /**
   * 刷新数据
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    // 不再分页，刷新时加载全部渠道
    await loadAllChannels();
  }, [loadAllChannels]);

  /**
   * 切换分组展开/折叠
   * @param {string} groupKey - 分组键
   */
  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups((prev) => {
      // 默认折叠（undefined ?? false = false），点击后变为 true（展开），再次点击变回 false（折叠）
      const isCurrently = prev[groupKey] ?? false;
      const next = !isCurrently;
      return { ...prev, [groupKey]: next };
    });
  }, []);

  const initialLoadRef = useRef(true);
  useEffect(() => {
    if (!initialLoadRef.current) return;
    initialLoadRef.current = false;
    // 不再分页，一次性加载全部渠道
    loadAllChannels().catch((reason) => showError(reason));
    loadChannelModels().catch(() => {});
  }, [loadAllChannels]);

  useEffect(() => {
    return () => {
      if (modelProbeTimerRef.current) clearInterval(modelProbeTimerRef.current);
    };
  }, []);

  /**
   * 编程探针：检测渠道是否支持工具调用（编程能力）
   * @param {Object} channel - 渠道对象
   */
  const probeChannel = useCallback(async (channel) => {
    showInfo(`正在探测渠道「${channel.name || '未命名'}#${channel.id}」的编程能力...`);
    try {
      const res = await API.get(`/api/channel/probe/${channel.id}`);
      const { data } = res.data;
      if (data && data.can_code) {
        showSuccess(`渠道「${channel.name || '未命名'}#${channel.id}」: 可编程 ✓ - ${data.reason}`);
      } else if (data) {
        showError(`渠道「${channel.name || '未命名'}#${channel.id}」: 不可编程 ✗ - ${data.reason}`);
      } else {
        showError(`探测失败: 无返回数据`);
      }
    } catch (error) {
      showError(`探测失败: ${error.message || '网络错误'}`);
    }
  }, []);

  /**
   * 渠道管理（启用/禁用/删除/优先级/权重）
   * @param {number} id - 渠道ID
   * @param {string} action - 操作类型
   * @param {number} idx - 当前页索引
   * @param {string} value - 新值
   */
  const manageChannel = useCallback(async (id, action, idx, value) => {
    try {
      // 从当前渠道列表中取出完整数据，避免单字段更新时把 name/group/models 等清空
      const fullChannel = channels.find((ch) => ch.id === id) || { id };
      const data = { ...fullChannel };
      // models 在前端被 processChannelData 转成了数组，发请求前要转回逗号分隔字符串
      if (Array.isArray(data.models)) {
        data.models = data.models.join(',');
      }
      let res;
      switch (action) {
        case 'delete':
          res = await API.delete(`/api/channel/${id}`);
          break;
        case 'enable':
          data.status = 1;
          // 启用/禁用只改 status，避免列表数据中空 key 经 PUT /api/channel/ 覆盖密钥
          res = data.is_temporary
            ? await API.put(`/api/playground/temporary-channels/${id}/reactivate`)
            : await API.put(`/api/channel/status/${id}`, { status: 1 });
          break;
        case 'disable':
          data.status = 2;
          res = await API.put(`/api/channel/status/${id}`, { status: 2 });
          break;
        case 'priority': {
          if (value === '') return;
          const priorityVal = parseInt(value);
          if (isNaN(priorityVal)) { showError('优先级必须是数字'); return; }
          data.priority = priorityVal;
          delete data.key; // 列表未加载密钥，禁止提交空 key
          res = await API.put('/api/channel/', data);
          break;
        }
        case 'weight': {
          if (value === '') return;
          const weightVal = parseInt(value);
          if (isNaN(weightVal)) { showError('权重必须是数字'); return; }
          data.weight = Math.max(0, weightVal);
          delete data.key;
          res = await API.put('/api/channel/', data);
          break;
        }
        default:
          return;
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('channel.messages.operation_success'));
        setChannels((prev) => {
          const newChannels = [...prev];
          if (action === 'delete') {
            // 通过渠道ID查找索引，避免分组模式下索引计算错误
            const channelIdx = newChannels.findIndex(ch => ch.id === id);
            if (channelIdx !== -1) {
              newChannels.splice(channelIdx, 1);
            }
          } else {
            // 通过渠道ID查找索引更新状态；临时渠道重新激活接口可能不返回完整渠道。
            const channelIdx = newChannels.findIndex(ch => ch.id === id);
            if (channelIdx !== -1) {
              newChannels[channelIdx].status = res.data.data?.status
                ?? (action === 'enable' ? 1 : action === 'disable' ? 2 : newChannels[channelIdx].status);
            }
          }
          return newChannels;
        });
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '操作失败');
    }
  }, [t, channels]);

  /**
   * 批量禁用相同标题的所有渠道
   * @param {Array} channelsToDisable - 待禁用的渠道列表
   * @param {string} groupName - 分组名称（用于提示）
   */
  const disableGroupChannels = useCallback(async (channelsToDisable, groupName) => {
    // 只禁用当前处于启用状态的渠道，避免对已禁用的渠道重复操作
    const enabled = (channelsToDisable || []).filter(ch => ch.status === 1);
    if (enabled.length === 0) {
      showError('没有可禁用的渠道');
      return;
    }
    try {
      let successCount = 0;
      let failCount = 0;
      const succeededIds = new Set();
      for (const ch of enabled) {
        try {
          // models 在前端被 processChannelData 转成了数组，发请求前要转回逗号分隔字符串
          const res = await API.put(`/api/channel/status/${ch.id}`, { status: 2 });
          if (res.data.success) {
            successCount++;
            succeededIds.add(ch.id);
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }
      // 仅更新请求成功的成员，失败项保留原状态。
      setChannels((prev) =>
        prev.map(ch => succeededIds.has(ch.id) ? { ...ch, status: 2 } : ch),
      );
      showSuccess(`已禁用 [${groupName}] 下 ${successCount} 个渠道${failCount > 0 ? `，失败 ${failCount} 个` : ''}`);
    } catch (error) {
      showError(error.message || '批量禁用失败');
    }
  }, []);

  const enableGroupChannels = useCallback(async (channelsToEnable, groupName) => {
    const disabled = channelsToEnable.filter(ch => ch.status !== 1);
    if (disabled.length === 0) {
      showError('没有可启用的渠道');
      return;
    }
    try {
      let successCount = 0;
      let failCount = 0;
      const succeededIds = new Set();
      for (const ch of disabled) {
        try {
          const res = ch.is_temporary
            ? await API.put(`/api/playground/temporary-channels/${ch.id}/reactivate`)
            : await API.put(`/api/channel/status/${ch.id}`, { status: 1 });
          if (res.data.success) {
            successCount++;
            succeededIds.add(ch.id);
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }
      setChannels((prev) =>
        prev.map(ch => succeededIds.has(ch.id) ? { ...ch, status: 1 } : ch),
      );
      showSuccess(`已启用 [${groupName}] 下 ${successCount} 个渠道${failCount > 0 ? `，失败 ${failCount} 个` : ''}`);
    } catch (error) {
      showError(error.message || '批量启用失败');
    }
  }, []);

  // 按 ID 删除当前可见分组，避免后端原始名称精确匹配漏掉首尾空格名称。
  const deleteGroupChannels = useCallback(async (channelsToDelete, groupName) => {
    if (!channelsToDelete || channelsToDelete.length === 0) {
      showError('没有可删除的渠道');
      return;
    }
    const deletedIds = new Set();
    let failedCount = 0;
    for (const channel of channelsToDelete) {
      try {
        const response = await API.delete(`/api/channel/${channel.id}`);
        if (response.data.success) deletedIds.add(channel.id);
        else failedCount += 1;
      } catch {
        failedCount += 1;
      }
    }
    setChannels((previous) => previous.filter((channel) => !deletedIds.has(channel.id)));
    if (deletedIds.size > 0) {
      showSuccess(`已删除 [${groupName}] 下 ${deletedIds.size} 个渠道${failedCount > 0 ? `，失败 ${failedCount} 个` : ''}`);
    } else {
      showError('批量删除失败');
    }
  }, []);

  /**
   * 渲染渠道状态徽标
   * @param {number} status - 状态码
   * @param {Function} t - 翻译函数
   * @returns {JSX.Element}
   */
  const renderStatus = useCallback((status) => {
    const cfg = {
      1: { text: t('channel.table.status_enabled'), bg: '#f0fdf4', color: '#16a34a', border: 'rgba(22,163,74,0.3)', icon: 'check circle' },
      2: { text: t('channel.table.status_disabled'), bg: '#fef2f2', color: '#dc2626', border: 'rgba(220,38,38,0.3)', icon: 'ban' },
      3: { text: t('channel.table.status_auto_disabled'), bg: '#fff7ed', color: '#ea580c', border: 'rgba(234,88,12,0.3)', icon: 'exclamation triangle' },
      4: { text: t('channel.table.status_temporary_invalid'), bg: '#fff7ed', color: '#c2410c', border: 'rgba(234,88,12,0.3)', icon: 'clock outline' },
    }[status] || { text: t('channel.table.status_unknown'), bg: '#f5f5f5', color: '#6b7280', border: 'rgba(0,0,0,0.12)', icon: 'question circle' };
    return (
      <Popup
        trigger={
          <span style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: 6,
            background: cfg.bg,
            color: cfg.color,
            fontSize: 12,
            fontWeight: 500,
            border: `1px solid ${cfg.border}`,
            whiteSpace: 'nowrap',
          }}>
            <Icon name={cfg.icon} style={{ margin: 0, fontSize: 11 }} /> {cfg.text}
          </span>
        }
        content={
          status === 2 ? t('channel.table.status_disabled_tip')
            : status === 3 ? t('channel.table.status_auto_disabled_tip')
            : status === 4 ? t('channel.table.status_temporary_invalid_tip')
            : t('channel.table.status_enabled')
        }
        basic
      />
    );
  }, [t]);

  /**
   * 渲染响应时间
   * @param {number} responseTime - 响应时间(ms)
   * @returns {JSX.Element}
   */
  const renderResponseTime = useCallback((responseTime) => {
    if (!responseTime || responseTime === 0) {
      return <span style={{ color: '#6b7280', fontSize: 11 }}>未测试</span>;
    }
    const time = (responseTime / 1000).toFixed(2) + 's';
    let color = 'green';
    if (responseTime > 5000) color = 'red';
    else if (responseTime > 3000) color = 'orange';
    else if (responseTime > 1000) color = 'blue';
    return <Label size='mini' basic color={color}><Icon name='bolt' /> {time}</Label>;
  }, []);

  /**
   * 搜索渠道
   */
  const searchChannels = useCallback(async () => {
    try {
      if (searchKeyword === '') {
        await loadAllChannels();
        return;
      }
      setSearching(true);
      const res = await API.get(`/api/channel/search?keyword=${searchKeyword}`);
      const { success, message, data } = res.data;
      if (success) {
        const localChannels = data.map(processChannelData);
        setChannels(localChannels);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '搜索失败');
    } finally {
      setSearching(false);
    }
  }, [searchKeyword, loadAllChannels, processChannelData]);

  /**
   * 测试单个渠道，带防抖和加载动效
   * @param {number} id - 渠道ID
   * @param {string} name - 渠道名称
   * @param {string} m - 测试模型
   */
  const testChannel = useCallback(async (id, name, m) => {
    if (testingChannelsRef.current.has(id)) return;
    testingChannelsRef.current.add(id);
    setTestingChannels(new Set(testingChannelsRef.current));
    try {
      const res = await API.get(`/api/channel/test/${id}?model=${m}`);
      const { success, message, time, model } = res.data;
      if (success) {
        setChannels((prev) => {
          const newChannels = [...prev];
          const channelIdx = newChannels.findIndex((channel) => channel.id === id);
          if (channelIdx !== -1) {
            newChannels[channelIdx].response_time = time * 1000;
            newChannels[channelIdx].test_time = Date.now() / 1000;
          }
          return newChannels;
        });
        showSuccess(t('channel.messages.test_success', { name, model, time, message }));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '测试失败');
    } finally {
      testingChannelsRef.current.delete(id);
      setTestingChannels(new Set(testingChannelsRef.current));
    }
  }, [t]);

  /**
   * 批量测试渠道
   * @param {string} scope - 测试范围
   */
  const testChannels = useCallback(async (scope) => {
    try {
      const res = await API.get(`/api/channel/test?scope=${scope}`);
      const { success, message } = res.data;
      if (success) {
        showInfo(t('channel.messages.test_all_started'));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '批量测试失败');
    }
  }, [t]);

  /**
   * 测试分组下所有渠道
   * @param {Array} channels - 分组内的渠道列表
   * @param {string} groupName - 分组名称
   * @param {string} model - 指定测试模型（可选）
   */
  const testGroupChannels = useCallback(async (channels, groupName, model) => {
    const enabledChannels = channels.filter(ch => ch.status === 1);
    if (enabledChannels.length === 0) {
      showError(`分组「${groupName}」没有启用的渠道`);
      return;
    }
    showInfo(`开始测试分组「${groupName}」的 ${enabledChannels.length} 个渠道...`);
    let passCount = 0;
    let failCount = 0;
    for (const ch of enabledChannels) {
      if (testingChannelsRef.current.has(ch.id)) continue;
      testingChannelsRef.current.add(ch.id);
      setTestingChannels(new Set(testingChannelsRef.current));
      const testModel = model || (Array.isArray(ch.models) ? ch.models[0] : (typeof ch.models === 'string' ? ch.models.split(',')[0]?.trim() : ''));
      if (!testModel) {
        testingChannelsRef.current.delete(ch.id);
        setTestingChannels(new Set(testingChannelsRef.current));
        failCount++;
        continue;
      }
      try {
        const res = await API.get(`/api/channel/test/${ch.id}?model=${testModel}`);
        const { success, time } = res.data;
        if (success) {
          passCount++;
          setChannels((prev) => {
            const newChannels = [...prev];
            const idx = newChannels.findIndex(c => c.id === ch.id);
            if (idx !== -1) {
              newChannels[idx].response_time = time * 1000;
              newChannels[idx].test_time = Date.now() / 1000;
            }
            return newChannels;
          });
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      } finally {
        testingChannelsRef.current.delete(ch.id);
        setTestingChannels(new Set(testingChannelsRef.current));
      }
    }
    showInfo(`分组「${groupName}」测试完成：${passCount} 成功，${failCount} 失败`);
  }, [t]);

  /**
   * 测试指定渠道的指定模型（单渠道多模型场景）
   * @param {number} id - 渠道ID
   * @param {string} name - 渠道名称
   * @param {string} model - 要测试的模型名称
   */
  const testChannelWithModel = useCallback(async (id, name, model) => {
    if (testingChannelsRef.current.has(id)) return;
    testingChannelsRef.current.add(id);
    setTestingChannels(new Set(testingChannelsRef.current));
    setTestingModelChannel({ channelId: id, model });
    try {
      const res = await API.get(`/api/channel/test/${id}?model=${model}`);
      const { success, message, time } = res.data;
      if (success) {
        setChannels((prev) => {
          const newChannels = [...prev];
          const channelIdx = newChannels.findIndex((channel) => channel.id === id);
          if (channelIdx !== -1) {
            newChannels[channelIdx].response_time = time * 1000;
            newChannels[channelIdx].test_time = Date.now() / 1000;
          }
          return newChannels;
        });
        showSuccess(t('channel.messages.test_success', { name, model, time, message }));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '测试失败');
    } finally {
      testingChannelsRef.current.delete(id);
      setTestingChannels(new Set(testingChannelsRef.current));
      setTestingModelChannel(null);
    }
  }, [t]);

  /**
   * 启动批量测试（一键测试所有模型）
   */
  const startBatchTest = useCallback(async (groupName, channelIDs, models) => {
    if (batchTestTask && batchTestTask.status === 'running') {
      showError('已有测试任务正在运行');
      return;
    }
    try {
      const res = await API.post('/api/channel/batch_test', {
        group_name: groupName,
        channel_ids: channelIDs,
        models: models,
        concurrency: 5,
      });
      const { success, message, task_id, total_models } = res.data;
      if (success) {
        const task = {
          taskId: task_id,
          total: total_models,
          actualModels: total_models,
          completed: 0, success: 0, failed: 0, percentage: 0, status: 'running', groupName,
        };
        setBatchTestTask(task);
        showSuccess(`已启动测试: ${total_models} 个模型（默认包含 3 项探测）`);
        pollBatchTestProgress(task_id, groupName);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '启动批量测试失败');
    }
  }, [batchTestTask]);

  /**
   * 轮询批量测试进度
   */
  const pollBatchTestProgress = useCallback((taskId, groupName) => {
    if (batchTestTimerRef.current) clearInterval(batchTestTimerRef.current);
    batchTestTimerRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/api/channel/batch_test/progress?task_id=${taskId}`);
        const { success, data } = res.data;
        if (success && data) {
          setBatchTestTask(prev => ({
            ...prev,
            completed: data.completed,
            success: data.success,
            failed: data.failed,
            percentage: data.percentage,
            status: data.status,
          }));
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(batchTestTimerRef.current);
            batchTestTimerRef.current = null;
            setTimeout(() => viewBatchReport(taskId, groupName), 500);
          }
        }
      } catch {
        clearInterval(batchTestTimerRef.current);
        batchTestTimerRef.current = null;
      }
    }, 1000);
  }, []);

  /**
   * 查看批次测试报告
   */
  const viewBatchReport = useCallback(async (batchIdOrEmpty, groupName) => {
    try {
      const params = batchIdOrEmpty ? `batch_id=${batchIdOrEmpty}` : `group_name=${encodeURIComponent(groupName)}`;
      const res = await API.get(`/api/channel/batch_test/reports?${params}`);
      const { success, data } = res.data;
      if (success && data) {
        setReportData(data);
        setReportTitle(groupName || '测试报告');
        setReportFilter('all');
        setReportSort({ field: '', direction: 'asc' });
        setReportModalOpen(true);
      } else {
        showError('获取报告失败');
      }
    } catch {
      showError('获取报告失败');
    }
  }, []);

  /**
   * 查看同一规范化分组的历史报告。报告接口仅支持精确原始名称，因此逐个查询成员名称后合并。
   */
  const viewHistoryReport = useCallback(async (group) => {
    const reportChannels = group?.channels || channels;
    const reportName = group?.name || '测试报告';
    try {
      const names = getChannelReportGroupNames(reportChannels);
      const responses = await Promise.allSettled(names.map((name) =>
        API.get(`/api/channel/batch_test/reports?group_name=${encodeURIComponent(name)}`)
      ));
      const successfulReports = responses
        .filter(({ status, value }) => status === 'fulfilled' && value.data.success && value.data.data)
        .map(({ value }) => value.data.data);
      if (successfulReports.length === 0) {
        showError('获取报告失败');
        return;
      }
      setReportData(mergeChannelReports(successfulReports));
      setReportTitle(reportName);
      setReportFilter('all');
      setReportSort({ field: '', direction: 'asc' });
      setReportModalOpen(true);
    } catch {
      showError('获取报告失败');
    }
  }, [channels]);

  /**
   * 加载指定规范化分组的最近报告并缓存。
   */
  const loadGroupReports = useCallback(async (group) => {
    if (loadedGroupReportsRef.current.has(group.key)) return;
    loadedGroupReportsRef.current.add(group.key);
    try {
      const names = getChannelReportGroupNames(group.channels);
      const responses = await Promise.allSettled(names.map((name) =>
        API.get(`/api/channel/batch_test/reports?group_name=${encodeURIComponent(name)}`)
      ));
      const reports = mergeChannelReports(responses
        .filter(({ status, value }) => status === 'fulfilled' && value.data.success && value.data.data)
        .map(({ value }) => value.data.data));
      setGroupReports((prev) => ({ ...prev, [group.key]: reports }));
    } catch {
      // 缓存加载失败不影响主界面展示
    }
  }, []);

  /**
   * 关闭进度条
   */
  const dismissBatchTest = useCallback(() => {
    if (batchTestTimerRef.current) {
      clearInterval(batchTestTimerRef.current);
      batchTestTimerRef.current = null;
    }
    setBatchTestTask(null);
  }, []);

  const formatProbeTime = useCallback((value) => {
    if (!value || value <= 0) return '-';
    return value < 1000 ? `${value}ms` : `${(value / 1000).toFixed(2)}s`;
  }, []);

  const sortModelProbeReports = useCallback((reports, sortState = modelProbeSort) => {
    const sorted = [...reports];
    sorted.sort((a, b) => {
      const isSuccess = (item) => !!(item.text?.success || item.vision?.success || item.tool?.success);
      const aSuccess = isSuccess(a);
      const bSuccess = isSuccess(b);
      if (aSuccess !== bSuccess) return aSuccess ? -1 : 1;
      const getValue = (item) => {
        if (sortState.field === 'text') return item.text?.elapsed_ms || 0;
        if (sortState.field === 'vision') return item.vision?.elapsed_ms || 0;
        if (sortState.field === 'tool') return item.tool?.elapsed_ms || 0;
        return item.response_time_ms || 0;
      };
      const av = getValue(a) || Number.MAX_SAFE_INTEGER;
      const bv = getValue(b) || Number.MAX_SAFE_INTEGER;
      return sortState.direction === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [modelProbeSort]);

  const loadModelProbeReport = useCallback(async (taskId) => {
    try {
      const res = await API.get(`/api/channel/model_probe/report?task_id=${taskId}`);
      const { success, data, message } = res.data;
      if (success) {
        setModelProbeReports(sortModelProbeReports(data || []));
      } else {
        showError(message || '获取模型探针报告失败');
      }
    } catch (error) {
      showError(error.message || '获取模型探针报告失败');
    }
  }, [sortModelProbeReports]);

  const pollModelProbeProgress = useCallback((taskId) => {
    if (modelProbeTimerRef.current) clearInterval(modelProbeTimerRef.current);
    modelProbeTimerRef.current = setInterval(async () => {
      try {
        const res = await API.get(`/api/channel/model_probe/progress?task_id=${taskId}`);
        const { success, data } = res.data;
        if (success && data) {
          setModelProbeTask(data);
          setModelProbeReports(sortModelProbeReports(data.reports || []));
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(modelProbeTimerRef.current);
            modelProbeTimerRef.current = null;
            await loadModelProbeReport(taskId);
          }
        }
      } catch {
        clearInterval(modelProbeTimerRef.current);
        modelProbeTimerRef.current = null;
      }
    }, 1200);
  }, [loadModelProbeReport, sortModelProbeReports]);

  const startModelProbe = useCallback(async () => {
    const modelName = modelProbeModel.trim();
    if (!modelName) {
      showError('请输入需要探测的模型 ID');
      return;
    }
    if (modelProbeTask?.status === 'running') {
      showError('已有模型探针任务正在运行');
      return;
    }
    try {
      setModelProbeReports([]);
      const res = await API.post('/api/channel/model_probe', {
        model: modelName,
        concurrency: parseInt(modelProbeConcurrency) || 5,
        scope: modelProbeScope,
      });
      const { success, message, task_id, total } = res.data;
      if (success) {
        const task = { task_id, model: modelName, total, completed: 0, matched: 0, failed: 0, percentage: 0, status: 'running', reports: [] };
        setModelProbeTask(task);
        showSuccess(`模型探针已启动，共 ${total} 个同名渠道组`);
        pollModelProbeProgress(task_id);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '启动模型探针失败');
    }
  }, [modelProbeConcurrency, modelProbeModel, modelProbeScope, modelProbeTask, pollModelProbeProgress]);

  const changeModelProbeSort = useCallback((field) => {
    setModelProbeSort((prev) => {
      const next = { field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc' };
      setModelProbeReports((reports) => sortModelProbeReports(reports, next));
      return next;
    });
  }, [sortModelProbeReports]);

  const renderProbeCapability = useCallback((capability) => {
    if (!capability) return <Label size='mini' basic>-</Label>;
    const ok = capability.success;
    return (
      <Popup
        trigger={
          <Label size='mini' basic color={ok ? 'green' : 'red'}>
            <Icon name={ok ? 'check' : 'close'} /> {formatProbeTime(capability.elapsed_ms)}
          </Label>
        }
        content={ok ? '探测通过' : (capability.error || '探测失败')}
        basic
      />
    );
  }, [formatProbeTime]);

  /**
   * 删除所有禁用渠道
   */
  const deleteAllDisabledChannels = useCallback(async () => {
    try {
      const res = await API.delete(`/api/channel/disabled`);
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('channel.messages.delete_disabled_success', { count: data }));
        await refresh();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '删除失败');
    }
  }, [refresh, t]);

  const [disableAllLoading, setDisableAllLoading] = useState(false);
  const disableAllEnabledChannels = useCallback(async () => {
    setDisableAllLoading(true);
    try {
      const res = await API.post('/api/channel/disable_all');
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(`已禁用 ${data} 个渠道`);
        await refresh();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '禁用失败');
    }
    setDisableAllLoading(false);
  }, [refresh]);

  // 批量模型校准 - 拉取所有渠道模型
  const calibratePreview = useCallback(async () => {
    setCalibrateLoading(true);
    setCalibrateResult(null);
    setCalibrateProgress(0);
    setCalibrateProgressText('正在拉取渠道列表...');
    try {
      // 进度更新：先快后慢，但不会卡死
      let progress = 0;
      const progressTimer = setInterval(() => {
        // 接近上限时减速，但持续增长到95
        if (progress < 50) {
          progress += Math.random() * 6;
        } else if (progress < 80) {
          progress += Math.random() * 3;
        } else if (progress < 95) {
          progress += Math.random() * 1;
        }
        progress = Math.min(progress, 95);
        setCalibrateProgress(Math.round(progress));
        if (progress >= 90) {
          setCalibrateProgressText(`正在拉取上游模型... ${Math.round(progress)}%（渠道较多请耐心等待）`);
        } else {
          setCalibrateProgressText(`正在拉取上游模型... ${Math.round(progress)}%`);
        }
      }, 800);

      const res = await API.post('/api/channel/model_calibrate/preview', {
        models: [],
        scope: calibrateScope,
      }, { timeout: 600000 }); // 10分钟超时

      clearInterval(progressTimer);
      setCalibrateProgress(100);
      setCalibrateProgressText('拉取完成，正在汇总...');

      const { success, message, data, total, failed, model_channels } = res.data;
      if (success) {
        const allModels = new Set();
        data.forEach(item => {
          (item.upstream_models || []).forEach(m => allModels.add(m));
        });
        const sortedModels = Array.from(allModels).sort();
        setCalibrateResult({ data, total, failed, model_channels, all_models: sortedModels });

        // 加载上次保存的选择（按账号缓存）
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const storageKey = `calibrate_selected_models_${user.id || 'default'}`;
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            const savedSet = new Set(JSON.parse(saved));
            const validSet = new Set();
            sortedModels.forEach(m => { if (savedSet.has(m)) validSet.add(m); });
            setCalibrateSelected(validSet);
          }
        } catch (e) {}

        showSuccess(`拉取完成：${sortedModels.length} 个模型，${total} 个渠道`);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '拉取失败');
    }
    setCalibrateLoading(false);
    setCalibrateProgress(0);
    setCalibrateProgressText('');
  }, [calibrateScope]);

  // 切换选中模型 + 保存到 localStorage
  const toggleCalibrateModel = useCallback((model) => {
    setCalibrateSelected(prev => {
      const next = new Set(prev);
      if (next.has(model)) next.delete(model);
      else next.add(model);
      // 按账号保存到 localStorage
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const storageKey = `calibrate_selected_models_${user.id || 'default'}`;
        localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
      } catch (e) {}
      return next;
    });
  }, []);

  // 批量模型校准 - 应用（用选中的模型列表做匹配）
  const calibrateApply = useCallback(async () => {
    if (calibrateSelected.size === 0) {
      showError('请至少选择一个模型');
      return;
    }
    setCalibrateApplying(true);
    try {
      const selectedModels = Array.from(calibrateSelected);
      const targetSet = new Set(selectedModels);
      const items = [];
      const noMatchIds = [];
      calibrateResult.data.forEach(item => {
        // 拉取失败的渠道也加入 noMatchIds，清空旧模型
        if (item.error) {
          noMatchIds.push(item.channel_id);
          return;
        }
        const matched = (item.upstream_models || []).filter(m => targetSet.has(m));
        if (matched.length > 0) {
          items.push({ channel_id: item.channel_id, matched_models: matched });
        } else {
          noMatchIds.push(item.channel_id);
        }
      });
      // 应用：有匹配的更新，无匹配的清空
      const res = await API.post('/api/channel/model_calibrate/apply', {
        items,
        clear_no_match: true,
        no_match_ids: noMatchIds,
      });
      const { success, message, updated, cleared } = res.data;
      if (success) {
        showSuccess(`已更新 ${updated} 个渠道，清空 ${cleared} 个无匹配渠道`);
        setCalibrateOpen(false);
        setCalibrateResult(null);
        setCalibrateSelected(new Set());
        await refresh();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '应用失败');
    }
    setCalibrateApplying(false);
  }, [calibrateResult, calibrateSelected, refresh]);

  /**
   * 更新单个渠道余额
   * @param {number} id - 渠道ID
   * @param {string} name - 渠道名称
   */
  const updateChannelBalance = useCallback(async (id, name) => {
    try {
      const res = await API.get(`/api/channel/update_balance/${id}/`);
      const { success, message, balance } = res.data;
      if (success) {
        setChannels((prev) => {
          const newChannels = [...prev];
          const channelIdx = newChannels.findIndex((channel) => channel.id === id);
          if (channelIdx !== -1) {
            newChannels[channelIdx].balance = balance;
            newChannels[channelIdx].balance_updated_time = Date.now() / 1000;
          }
          return newChannels;
        });
        showSuccess(t('channel.messages.balance_update_success', { name }));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '更新余额失败');
    }
  }, [t]);

  /**
   * 更新所有渠道余额
   */
  const updateAllChannelsBalance = useCallback(async () => {
    setUpdatingBalance(true);
    try {
      const res = await API.get(`/api/channel/update_balance`);
      const { success, message } = res.data;
      if (success) {
        showInfo(t('channel.messages.all_balance_updated'));
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '批量更新余额失败');
    } finally {
      setUpdatingBalance(false);
    }
  }, [t]);

  /**
   * 打开单个渠道的模型管理弹窗
   * @param {Object} channel - 渠道对象
   */
  const openModelModal = useCallback((channel) => {
    setModelModalChannel(channel);
    setModelModalModels(channel.models ? channel.models.join('\n') : '');
    setModelModalOpen(true);
  }, []);

  /**
   * 关闭模型管理弹窗
   */
  const closeModelModal = useCallback(() => {
    setModelModalOpen(false);
    setModelModalChannel(null);
    setModelModalModels('');
  }, []);

  /**
   * 从上游一键导入所有模型
   */
  const fetchUpstreamModels = useCallback(async () => {
    if (!modelModalChannel?.id) return;
    setFetchModelsLoading(true);

    // 显示进度 toast
    const toastId = Date.now();
    const updateProgress = (msg) => {
      import('react-toastify').then(({ toast }) => {
        if (toast.isActive(toastId)) {
          toast.update(toastId, { render: msg, isLoading: true });
        }
      });
    };

    // 启动进度
    import('react-toastify').then(({ toast }) => {
      toast.loading(`正在获取「${modelModalChannel.name || '未命名'}」的上游模型列表...`, { toastId });
    });

    try {
      const res = await API.get(`/api/channel/fetch_models/${modelModalChannel.id}`);
      const { success, message, data } = res.data;
      if (!success || !data || data.length === 0) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: message || '上游未返回任何模型', type: 'error', isLoading: false, autoClose: 5000 });
        });
        return;
      }

      updateProgress(`已获取 ${data.length} 个模型，正在保存到数据库...`);

      const saveRes = await API.put(`/api/channel/models/${modelModalChannel.id}`, {
        models: data.join(','),
        action: 'set',
      });
      const { success: ss, message: sm, data: updated } = saveRes.data;
      if (ss) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: `已导入 ${data.length} 个模型并自动保存 ✓`, type: 'success', isLoading: false, autoClose: 5000 });
        });
        setChannels((prev) => {
          const newChannels = [...prev];
          const idx = newChannels.findIndex(ch => ch.id === modelModalChannel.id);
          if (idx !== -1) {
            newChannels[idx] = { ...newChannels[idx], ...updated };
            if (updated?.models) {
              newChannels[idx].models = updated.models.split(',').filter(Boolean);
            }
          }
          return newChannels;
        });
        closeModelModal();
      } else {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: sm || '自动保存失败', type: 'error', isLoading: false, autoClose: 5000 });
        });
      }
    } catch (error) {
      import('react-toastify').then(({ toast }) => {
        toast.update(toastId, { render: error.message || '获取模型失败', type: 'error', isLoading: false, autoClose: 5000 });
      });
    } finally {
      setFetchModelsLoading(false);
    }
  }, [modelModalChannel, closeModelModal]);

  /**
   * 更新单个渠道的模型列表
   * @param {string} action - 操作类型: "set" | "add" | "remove"
   */
  const updateChannelModels = useCallback(async (action) => {
    if (!modelModalChannel) return;
    setModelModalLoading(true);
    try {
      const res = await API.put(`/api/channel/models/${modelModalChannel.id}`, {
        models: modelModalModels,
        action: action,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess('模型列表已更新');
        // 更新本地状态
        setChannels((prev) => {
          const newChannels = [...prev];
          const idx = newChannels.findIndex(ch => ch.id === modelModalChannel.id);
          if (idx !== -1) {
            newChannels[idx] = { ...newChannels[idx], ...data };
            // 重新解析模型列表
            if (data.models) {
              newChannels[idx].models = data.models.split(',').filter(Boolean);
            }
          }
          return newChannels;
        });
        closeModelModal();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message || '更新模型失败');
    } finally {
      setModelModalLoading(false);
    }
  }, [modelModalChannel, modelModalModels, closeModelModal]);

  /**
   * 打开分组批量管理弹窗
   * @param {string} group - 分组名称（按渠道名称匹配）
   */
  const openGroupModelModal = useCallback((group) => {
    const groupKey = normalizeChannelGroupName(group).key;
    const groupChannels = channels.filter((channel) => normalizeChannelGroupName(channel.name).key === groupKey);
    setGroupModelModalGroup(group);
    setGroupModelModalModels(collectChannelModels(groupChannels).join('\n'));
    setGroupModelModalAction('add');
    setGroupModelModalOpen(true);
  }, [channels]);

  /**
   * 关闭分组批量管理弹窗
   */
  const closeGroupModelModal = useCallback(() => {
    setGroupModelModalOpen(false);
    setGroupModelModalGroup('');
    setGroupModelModalModels('');
  }, []);

  /**
   * 分组管理弹窗：从上游一键导入所有模型
   */
  const fetchGroupUpstreamModels = useCallback(async () => {
    const groupName = groupModelModalGroup || '未命名';
    setFetchModelsLoading(true);

    const toastId = Date.now();
    const updateProgress = (msg) => {
      import('react-toastify').then(({ toast }) => {
        if (toast.isActive(toastId)) {
          toast.update(toastId, { render: msg, isLoading: true });
        }
      });
    };

    import('react-toastify').then(({ toast }) => {
      toast.loading(`正在加载「${groupName}」的渠道列表...`, { toastId });
    });

    try {
      // 拉取全量渠道（分页模式下本地 channels 不全）
      const allRes = await API.get('/api/channel/?p=0&size=10000');
      const { success: as, data: allData } = allRes.data;
      if (!as || !allData) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: '获取渠道列表失败', type: 'error', isLoading: false, autoClose: 5000 });
        });
        return;
      }
      const groupKey = normalizeChannelGroupName(groupName).key;
      const allGroupChannels = allData.filter(ch =>
        normalizeChannelGroupName(ch.name).key === groupKey
      );
      const enabledChannels = allGroupChannels.filter(ch => ch.status === 1);
      if (allGroupChannels.length === 0) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: '该分组没有任何渠道', type: 'error', isLoading: false, autoClose: 5000 });
        });
        return;
      }
      const groupChannels = enabledChannels.length > 0 ? enabledChannels : allGroupChannels;
      const target = groupChannels[0];

      updateProgress(`正在获取「${target.name}」的上游模型列表...`);

      const fetchRes = await API.get(`/api/channel/fetch_models/${target.id}`);
      const { success: fs, data: models, message: fm } = fetchRes.data;
      if (!fs || !models || models.length === 0) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: fm || '上游未返回任何模型', type: 'error', isLoading: false, autoClose: 5000 });
        });
        return;
      }

      updateProgress(`已获取 ${models.length} 个模型，正在保存到 ${allGroupChannels.length} 个渠道...`);

      let updatedCount = 0;
      for (const channel of allGroupChannels) {
        const saveRes = await API.put(`/api/channel/models/${channel.id}`, {
          models: models.join(','),
          action: 'set',
        });
        if (saveRes.data.success) updatedCount += 1;
      }
      if (updatedCount > 0) {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: `已从「${target.name}」导入 ${models.length} 个模型，共更新 ${updatedCount} 个渠道 ✓`, type: 'success', isLoading: false, autoClose: 5000 });
        });
        await refresh();
        closeGroupModelModal();
      } else {
        import('react-toastify').then(({ toast }) => {
          toast.update(toastId, { render: '自动保存失败', type: 'error', isLoading: false, autoClose: 5000 });
        });
      }
    } catch (error) {
      import('react-toastify').then(({ toast }) => {
        toast.update(toastId, { render: error.message || '获取模型失败', type: 'error', isLoading: false, autoClose: 5000 });
      });
    } finally {
      setFetchModelsLoading(false);
    }
  }, [groupModelModalGroup, refresh, closeGroupModelModal]);

  /**
   * 批量更新分组的模型列表
   */
  const updateGroupModels = useCallback(async () => {
    if (!groupModelModalModels) {
      showError('请输入模型名称');
      return;
    }
    const groupKey = normalizeChannelGroupName(groupModelModalGroup).key;
    const targetChannels = channels.filter((channel) => normalizeChannelGroupName(channel.name).key === groupKey);
    setGroupModelModalLoading(true);
    try {
      let updatedCount = 0;
      let failedCount = 0;
      for (const channel of targetChannels) {
        try {
          const response = await API.put(`/api/channel/models/${channel.id}`, {
            models: groupModelModalModels,
            action: groupModelModalAction,
          });
          if (response.data.success) updatedCount += 1;
          else failedCount += 1;
        } catch {
          failedCount += 1;
        }
      }
      if (updatedCount > 0) {
        showSuccess(`已更新 ${updatedCount} 个渠道${failedCount > 0 ? `，失败 ${failedCount} 个` : ''}`);
        await refresh();
        closeGroupModelModal();
      } else {
        showError('批量更新模型失败');
      }
    } finally {
      setGroupModelModalLoading(false);
    }
  }, [groupModelModalGroup, groupModelModalModels, groupModelModalAction, channels, refresh, closeGroupModelModal]);

  /**
   * 关键词变化处理
   */
  const handleKeywordChange = useCallback((e, { value }) => {
    setSearchKeyword(value.trim());
  }, []);

  /**
   * 排序渠道
   * @param {string} key - 排序字段
   */
  const sortChannel = useCallback((key) => {
    setChannels((prev) => {
      if (prev.length === 0) return prev;
      const sorted = [...prev];
      sorted.sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        const an = Number(av);
        const bn = Number(bv);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return ('' + (av ?? '')).localeCompare('' + (bv ?? ''));
      });
      if (sorted[0].id === prev[0].id) sorted.reverse();
      return sorted;
    });
  }, []);

  /**
   * 计算分组后的渠道结构（按渠道名称分组）
   * 返回 [{ key, name, channels: [...], enabledCount, lastSuccessTime }]
   * 相同名称的渠道自动归为一组，按启用状态、最近成功调用排序，"未命名"排最后
   */
  const groupedChannels = useMemo(() => groupChannelsByName(channels), [channels]);

  // 预加载全部同名渠道组的最近测试报告（串行，避免并发请求过多）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const g of groupedChannels) {
        if (cancelled) return;
        await loadGroupReports(g);
      }
    })();
    return () => { cancelled = true; };
  }, [groupedChannels, loadGroupReports]);

  // 删除确认弹窗状态
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);

  // 打开删除确认弹窗
  const openDeleteConfirm = (channel, idx) => {
    setChannelToDelete({ channel, idx });
    setDeleteConfirmOpen(true);
  };

  // 关闭删除确认弹窗
  const closeDeleteConfirm = () => {
    setChannelToDelete(null);
    setDeleteConfirmOpen(false);
  };

  // 执行删除操作
  const confirmDelete = async () => {
    if (channelToDelete) {
      await manageChannel(channelToDelete.channel.id, 'delete', channelToDelete.idx);
      closeDeleteConfirm();
    }
  };

  // 渲染渠道行（共享的表格行渲染函数）
  const renderChannelRow = (channel, idx, groupName = null) => {
    const isHovered = hoveredRow === channel.id;
    const isTesting = testingChannels.has(channel.id);
    const isDeleteConfirmOpen = deleteConfirmOpen[channel.id] || false;
    const testButtonStyle = {
      ...styles.button,
      boxShadow: isHovered ? '0 6px 16px rgba(22,163,74,0.12)' : 'none',
    };
    // 从缓存的最近报告中提取该渠道测试成功的模型（去重、按报告出现顺序）
    const availableModels = (() => {
      if (!groupName) return [];
      const reports = groupReports[normalizeChannelGroupName(groupName).key] || [];
      const models = [];
      reports.forEach((r) => {
        if (r.channel_id === channel.id && r.success && !models.includes(r.model_name)) {
          models.push(r.model_name);
        }
      });
      return models;
    })();

    return (
      <Table.Row
        key={channel.id}
        style={{
          ...styles.tableRow,
          ...(isHovered ? styles.tableRowHover : {}),
        }}
        onMouseEnter={() => setHoveredRow(channel.id)}
        onMouseLeave={() => setHoveredRow(null)}
      >
        <Table.Cell style={{ minWidth: 240, padding: '18px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{
                padding: '4px 8px',
                borderRadius: 999,
                background: '#f5f5f5',
                color: '#6b7280',
                fontSize: 12,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}>#{channel.id}</span>
              <span style={{
                fontWeight: 800,
                color: '#18181b',
                fontSize: 15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{channel.name || t('channel.table.no_name')}</span>
              {(!channel.models || channel.models.length === 0) && (
                <Label size='mini' basic color='grey' style={{ marginLeft: 4 }}>⚠ 无模型</Label>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {renderType(channel.type, t)}
              {channel.is_temporary && (
                <Popup
                  basic
                  content={t('channel.table.temporary_tip')}
                  trigger={
                    <Label size='mini' basic color='orange'>
                      <Icon name='clock outline' />
                      {t('channel.table.temporary')}
                    </Label>
                  }
                />
              )}
              {renderGroup(channel.group)}
            </div>
          </div>
        </Table.Cell>

        <Table.Cell style={{ minWidth: 160, padding: '18px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {renderStatus(channel.status)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Popup
                on='click'
                pinned
                trigger={
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: '#f5f5f5', color: '#6b7280', border: '1px solid #e2e8f0',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    <Icon name='arrow up' style={{ margin: 0, fontSize: 10 }} /> {channel.priority}
                  </span>
                }
                content={
                  <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>优先级（数值越大越优先）</div>
                    <Input
                      type='number' size='mini' defaultValue={channel.priority}
                      onBlur={(e) => manageChannel(channel.id, 'priority', idx, e.target.value)}
                      action={{ icon: 'check', size: 'mini', color: 'blue' }}
                      style={{ width: 120 }}
                    />
                  </div>
                }
              />
              <Popup
                on='click'
                pinned
                trigger={
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: '#f5f5f5', color: '#6b7280', border: '1px solid #e2e8f0',
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    <Icon name='balance scale' style={{ margin: 0, fontSize: 10 }} /> {channel.weight}
                  </span>
                }
                content={
                  <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>权重（数值越大命中概率越高）</div>
                    <Input
                      type='number' size='mini' defaultValue={channel.weight}
                      onBlur={(e) => manageChannel(channel.id, 'weight', idx, e.target.value)}
                      action={{ icon: 'check', size: 'mini', color: 'blue' }}
                      style={{ width: 120 }}
                    />
                  </div>
                }
              />
            </div>
            {/* 展示最近报告中该渠道可用的模型，最多 2 个名称，第 3 个位置显示剩余数量 */}
            {availableModels.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ color: '#6b7280', fontSize: 12, minWidth: 42 }}>可用</span>
                {availableModels.slice(0, 2).map((m) => (
                  <span key={m} title={m} style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                    background: '#f0fdf4', color: '#16a34a', fontSize: 10, fontWeight: 500,
                    border: '1px solid rgba(22,163,74,0.3)', maxWidth: 120, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{m}</span>
                ))}
                {availableModels.length > 2 && (
                  <span title={`共 ${availableModels.length} 个可用模型`} style={{
                    display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                    background: '#f5f5f5', color: '#6b7280', fontSize: 10, fontWeight: 500,
                    border: '1px solid #e2e8f0', cursor: 'help',
                  }}>+{availableModels.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </Table.Cell>

        <Table.Cell style={{ minWidth: 190, padding: '18px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#6b7280', fontSize: 12, minWidth: 42 }}>响应</span>
              <Popup content={renderTimestamp(channel.test_time)} trigger={renderResponseTime(channel.response_time)} basic />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#6b7280', fontSize: 12, minWidth: 42 }}>余额</span>
              <Popup
                trigger={
                  <button
                    type='button'
                    onClick={() => updateChannelBalance(channel.id, channel.name)}
                    style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
                    title='点击更新余额'
                  >
                    {renderBalance(channel.type, channel.balance, t)}
                  </button>
                }
                content={t('channel.table.click_to_update')}
                basic
              />
            </div>
          </div>
        </Table.Cell>

        <Table.Cell style={{ minWidth: 150, padding: '18px 14px' }}>
          {renderLastSuccessTime(channel.last_success_time)}
        </Table.Cell>

        <Table.Cell style={{ minWidth: 360, padding: '18px 14px' }}>
          {renderModelTags(channel.models, () => openModelModal(channel))}
        </Table.Cell>

        <Table.Cell style={{ minWidth: 210, padding: '18px 14px' }}>
          {(!channel.models || channel.models.length <= 1) ? (
            <Button
              positive
              size='mini'
              loading={isTesting}
              disabled={isTesting}
              onClick={() => testChannel(channel.id, channel.name, channel.test_model)}
              style={testButtonStyle}
            >
              <Icon name='bolt' /> {t('channel.buttons.test')}
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Button
                positive
                size='mini'
                loading={isTesting}
                disabled={isTesting || (batchTestTask?.status === 'running')}
                onClick={() => startBatchTest(channel.name, [channel.id], channel.models)}
                style={testButtonStyle}
              >
                <Icon name='bolt' /> 全部 {channel.models.length}
              </Button>
            </div>
          )}
        </Table.Cell>

        <Table.Cell style={{ minWidth: 260, padding: '18px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button
              size='mini'
              basic
              color={channel.status === 1 ? 'red' : 'green'}
              onClick={() => manageChannel(channel.id, channel.status === 1 ? 'disable' : 'enable', idx)}
              style={styles.button}
            >
              <Icon name={channel.status === 1 ? 'ban' : 'check'} />
              {channel.status === 1 ? t('channel.buttons.disable') : t('channel.buttons.enable')}
            </Button>
            <Button as={Link} to={'/channel/edit/' + channel.id} size='mini' primary style={styles.button}>
              <Icon name='edit' /> {t('channel.buttons.edit')}
            </Button>
            <Button
              size='mini'
              basic
              negative
              onClick={() => openDeleteConfirm(channel, idx)}
              style={styles.button}
            >
              <Icon name='trash' /> 删除
            </Button>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  const colSpan = 7;

  // 手机端卡片渲染
  const renderChannelCard = (channel, idx, groupName = null) => {
    const isTesting = testingChannels.has(channel.id);
    const models = channel.models || [];
    const isEnabled = channel.status === 1;
    const hasNoModels = !channel.models || channel.models.length === 0;
    return (
      <div key={channel.id} style={{
        background: '#fff', borderRadius: 10, marginBottom: 8,
        border: '1px solid rgba(0,0,0,0.06)',
        borderLeft: hasNoModels ? '4px solid #9ca3af' : `4px solid ${isEnabled ? '#16a34a' : '#dc2626'}`,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)', overflow: 'hidden',
      }}>
        {/* 头部：ID + 名称 + 状态 */}
        <div style={{ padding: '10px 12px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
              <span style={{
                padding: '2px 6px', borderRadius: 999, background: '#f5f5f5',
                color: '#6b7280', fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}>#{channel.id}</span>
              <span style={{
                fontWeight: 800, color: '#18181b', fontSize: 13,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{channel.name || t('channel.table.no_name')}</span>
            </div>
            <span style={{
              padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, flexShrink: 0,
              background: isEnabled ? '#dcfce7' : '#fee2e2',
              color: isEnabled ? '#16a34a' : '#dc2626',
            }}>{isEnabled ? '● 启用' : '○ 禁用'}</span>
            {hasNoModels && (
              <span style={{
                marginLeft: 4, padding: '2px 6px', borderRadius: 999,
                fontSize: 10, fontWeight: 700, flexShrink: 0,
                background: '#f3f4f6', color: '#6b7280',
              }}>⚠ 无模型</span>
            )}
          </div>

          {/* 信息行 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11, color: '#9ca3af' }}>
            {channel.group && <span>分组: {channel.group}</span>}
            <span>响应: {channel.response_time > 0 ? `${channel.response_time}ms` : '-'}</span>
            {channel.balance > 0 && <span>余额: {renderBalance(channel.type, channel.balance, t)}</span>}
          </div>

          {/* 模型标签 */}
          {models.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {models.slice(0, 4).map((m, i) => (
                  <span key={i} style={{
                    padding: '1px 5px', borderRadius: 3, background: '#f0f4f8',
                    color: '#4f63a8', fontSize: 10, maxWidth: 90,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{m}</span>
                ))}
                {models.length > 4 && (
                  <span onClick={() => openModelModal(channel)} style={{
                    padding: '1px 5px', borderRadius: 3, background: '#f5f5f5',
                    color: '#6b7280', fontSize: 10, cursor: 'pointer',
                  }}>+{models.length - 4}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 操作按钮 - 网格布局 */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1,
          background: 'rgba(0,0,0,0.04)', borderTop: '1px solid rgba(0,0,0,0.05)',
        }}>
          <button
            onClick={() => {
              if (models.length > 1) { startBatchTest(channel.name, [channel.id], models); }
              else { testChannel(channel.id, channel.name, channel.test_model); }
            }}
            disabled={isTesting}
            style={{
              padding: '8px 4px', border: 'none', background: '#fff',
              fontSize: 11, color: '#16a34a', fontWeight: 600, cursor: isTesting ? 'wait' : 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            <Icon name='bolt' style={{ margin: 0, fontSize: 14 }} />
            {isTesting ? '测试中' : '测试'}
          </button>
          <button
            onClick={() => manageChannel(channel.id, isEnabled ? 'disable' : 'enable', idx)}
            style={{
              padding: '8px 4px', border: 'none', background: '#fff',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              color: isEnabled ? '#dc2626' : '#16a34a',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            <Icon name={isEnabled ? 'ban' : 'check'} style={{ margin: 0, fontSize: 14 }} />
            {isEnabled ? '禁用' : '启用'}
          </button>
          <button
            onClick={() => { window.location.href = '/channel/edit/' + channel.id; }}
            style={{
              padding: '8px 4px', border: 'none', background: '#fff',
              fontSize: 11, color: '#2563eb', fontWeight: 600, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            <Icon name='edit' style={{ margin: 0, fontSize: 14 }} />
            编辑
          </button>
          <button
            onClick={() => openDeleteConfirm(channel, idx)}
            style={{
              padding: '8px 4px', border: 'none', background: '#fff',
              fontSize: 11, color: '#dc2626', fontWeight: 600, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            <Icon name='trash' style={{ margin: 0, fontSize: 14 }} />
            删除
          </button>
        </div>
      </div>
    );
  };

  // 手机端分组卡片渲染
  const renderMobileChannelList = () => {
    if (groupedChannels.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          <Icon
            name='inbox'
            size='large'
            style={{ marginBottom: 12, display: 'block', opacity: 0.5 }}
          />
          {loading ? (
            <>
              <Icon name='spinner' loading /> 加载中...
            </>
          ) : (
            '暂无渠道数据'
          )}
        </div>
      );
    }
    return (
      <div>
        {groupedChannels.map((g) => {
          const isExpanded = expandedGroups[g.key] ?? false;
          const enabledCount = g.channels.filter(
            (ch) => ch.status === 1
          ).length;
          const disabledCount = g.channels.length - enabledCount;
          const noModelCount = g.channels.filter(
            (ch) => !ch.models || ch.models.length === 0
          ).length;
          return (
            <section key={g.key} className='studio-mobile-channel-group'>
              <button
                type='button'
                className={`studio-mobile-channel-group__toggle${
                  isExpanded ? ' is-expanded' : ''
                }`}
                aria-expanded={isExpanded}
                aria-controls={`mobile-channel-group-${encodeURIComponent(
                  g.key
                )}`}
                onClick={() => toggleGroup(g.key)}
              >
                <span className='studio-mobile-channel-group__title'>
                  <strong>{g.name}</strong>
                  <small>{g.channels.length} 个渠道</small>
                </span>
                <span className='studio-mobile-channel-group__summary'>
                  {enabledCount > 0 && (
                    <span className='is-enabled'>启用 {enabledCount}</span>
                  )}
                  {disabledCount > 0 && (
                    <span className='is-disabled'>禁用 {disabledCount}</span>
                  )}
                  {noModelCount > 0 && <span>无模型 {noModelCount}</span>}
                  <Icon name={isExpanded ? 'angle up' : 'angle down'} />
                </span>
              </button>
              <div className='studio-mobile-channel-group__actions'>
                <button
                  type='button'
                  disabled={batchTestTask?.status === 'running'}
                  onClick={() => {
                    const models = collectChannelModels(g.channels);
                    if (models.length === 0) {
                      showError(`分组「${g.name}」没有模型`);
                      return;
                    }
                    startBatchTest(
                      g.name,
                      g.channels.map((channel) => channel.id),
                      models
                    );
                  }}
                >
                  <Icon name='bolt' /> 测试
                </button>
                <button
                  type='button'
                  onClick={() => openGroupModelModal(g.name)}
                >
                  <Icon name='cubes' /> 模型
                </button>
                <button
                  type='button'
                  onClick={() =>
                    g.enabledCount > 0
                      ? disableGroupChannels(g.channels, g.name)
                      : enableGroupChannels(g.channels, g.name)
                  }
                >
                  <Icon name={g.enabledCount > 0 ? 'ban' : 'check circle'} />
                  {g.enabledCount > 0 ? '禁用' : '启用'}
                </button>
                <button type='button' onClick={() => viewHistoryReport(g)}>
                  <Icon name='chart bar' /> 报告
                </button>
              </div>
              {isExpanded && (
                <div id={`mobile-channel-group-${encodeURIComponent(g.key)}`}>
                  {g.channels.map((channel, index) =>
                    renderChannelCard(channel, index, g.name)
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    );
  };

  return (
    <div className='studio-table-page' style={styles.page}>
      {showPrompt && (
        <Message
          onDismiss={() => {
            setShowPrompt(false);
            setPromptShown(promptID);
          }}
          style={{
            borderRadius: 10,
            border: '1px solid #bfdbfe',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          }}
        >
          {t('channel.balance_notice')}
          <br />
          {t('channel.test_notice')}
        </Message>
      )}

      <StudioHero
        icon='server'
        kicker='AI STUDIO / CHANNELS'
        title={t('channel.studio.title')}
        description={t('channel.studio.subtitle')}
        className='studio-management-hero'
      />

      <section className='studio-management-overview'>
        <div>
          <span className='studio-section-eyebrow'>CHANNEL FLEET</span>
          <h2>{t('channel.studio.overview_title')}</h2>
          <p>{t('channel.studio.overview_subtitle')}</p>
        </div>
        <div className='studio-management-overview__stats'>
          <StudioStat label={t('channel.studio.total')} value={channels.length} />
          <StudioStat label={t('channel.studio.enabled')} value={channels.filter((channel) => channel.status === 1).length} tone='success' />
          <StudioStat label={t('channel.studio.disabled')} value={channels.filter((channel) => channel.status !== 1).length} tone='danger' />
        </div>
      </section>

      <section className='studio-management-toolbar'>
        <div className='studio-management-toolbar__heading'>
          <div>
            <span className='studio-section-eyebrow'>CHANNEL CONTROLS</span>
            <h3>{t('channel.studio.search')}</h3>
          </div>
          <Icon name='sliders horizontal' />
        </div>
        <Form onSubmit={searchChannels}>
          <Form.Group className='studio-management-search-row'>
            <Form.Input
              icon='search'
              placeholder={t('channel.search')}
              value={searchKeyword}
              loading={searching}
              onChange={handleKeywordChange}
              size='small'
              width={6}
              style={{ borderRadius: 8 }}
            />
            <Form.Button primary size='small' type='submit' style={styles.button}>
              <Icon name='search' /> {t('channel.studio.search')}
            </Form.Button>
          </Form.Group>
        </Form>

      {/* 批量测试进度条 */}
      {batchTestTask && (
        <div style={{
          marginBottom: 16,
          padding: '14px 18px',
          borderRadius: 14,
          background: batchTestTask.status === 'running'
            ? 'linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(14,165,233,0.04) 100%)'
            : 'linear-gradient(135deg, rgba(22,163,74,0.06) 0%, rgba(34,197,94,0.04) 100%)',
          border: `1px solid ${batchTestTask.status === 'running' ? 'rgba(191,219,254,0.8)' : 'rgba(187,247,208,0.8)'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
        }}>
          <div className='studio-batch-progress__header'>
            <div className='studio-batch-progress__status'>
              <Icon
                name={batchTestTask.status === 'running' ? 'spinner' : 'check circle'}
                loading={batchTestTask.status === 'running'}
                style={{ color: batchTestTask.status === 'running' ? '#18181b' : '#16a34a', fontSize: 16, margin: 0 }}
              />
              <span style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>
                {batchTestTask.status === 'running' ? '正在测试' : '测试完成'}: {batchTestTask.groupName}
              </span>
              <span style={{
                padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: batchTestTask.status === 'running' ? 'rgba(0,0,0,0.06)' : '#f0fdf4',
                color: batchTestTask.status === 'running' ? '#18181b' : '#16a34a',
              }}>
                {batchTestTask.completed}/{batchTestTask.total}
              </span>
              <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>
                <Icon name='check' style={{ margin: 0, fontSize: 10 }} /> {batchTestTask.success}
              </span>
              <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
                <Icon name='close' style={{ margin: 0, fontSize: 10 }} /> {batchTestTask.failed}
              </span>
            </div>
            <div className='studio-batch-progress__actions'>
              {(batchTestTask.status === 'completed' || batchTestTask.status === 'failed') && (
                <Button size='mini' basic color='blue'
                  onClick={() => viewBatchReport(batchTestTask.taskId, batchTestTask.groupName)}
                  style={{ borderRadius: 8, fontSize: 11, padding: '4px 10px' }}
                >
                  <Icon name='chart bar' style={{ margin: 0 }} /> 查看报告
                </Button>
              )}
              <Button size='mini' basic icon='close'
                aria-label='关闭批量测试进度'
                onClick={dismissBatchTest}
                style={{ borderRadius: 8, padding: '4px 8px' }}
              />
            </div>
          </div>
          <Progress
            percent={Math.round(batchTestTask.percentage)}
            indicating={batchTestTask.status === 'running'}
            success={batchTestTask.status === 'completed'}
            error={batchTestTask.status === 'failed'}
            size='tiny'
            style={{ margin: 0, borderRadius: 6 }}
          />
        </div>
      )}

        {/* 功能操作 */}
        <div className='studio-management-toolbar__actions' style={isMobile ? { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, padding: '8px 10px' } : undefined}>
        <Button as={Link} to='/channel/add' primary loading={loading} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}>
          <Icon name='plus' /> {t('channel.buttons.add')}
        </Button>
        <Button loading={loading} onClick={() => testChannels('all')} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}>
          <Icon name='bolt' /> {t('channel.buttons.test_all')}
        </Button>
        <Button loading={loading} onClick={() => testChannels('disabled')} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}>
          <Icon name='stethoscope' /> {t('channel.buttons.test_disabled')}
        </Button>
        <Popup
          trigger={<Button loading={loading} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}><Icon name='trash alternate' /> {t('channel.buttons.delete_disabled')}</Button>}
          on='click'
          flowing
          hoverable
        >
          <Button loading={loading} negative onClick={deleteAllDisabledChannels} style={styles.button}>
            <Icon name='exclamation triangle' /> {t('channel.buttons.confirm_delete_disabled')}
          </Button>
        </Popup>
        <Popup
          trigger={<Button loading={disableAllLoading} size={isMobile ? 'tiny' : 'mini'} basic color='red' style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}><Icon name='pause circle' /> 一键禁用所有</Button>}
          on='click'
          flowing
          hoverable
        >
          <div style={{ padding: 4, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>确认禁用所有启用的渠道？</p>
            <Button negative size='mini' loading={disableAllLoading} onClick={disableAllEnabledChannels}>
              <Icon name='ban' /> 确认禁用
            </Button>
          </div>
        </Popup>
        <Button loading={loading} onClick={() => setImportModalOpen(true)} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button} color="teal">
          <Icon name='cloud download' /> 一键导入
        </Button>
        {isRoot() && (
          <Button onClick={() => setModelPriorityModalOpen(true)} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button} color='blue'>
            <Icon name='sort amount down' /> {t('channel.model_priority.button')}
          </Button>
        )}
        {isRoot() && (
          <Button onClick={() => setModelTierModalOpen(true)} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button} color='green'>
            <Icon name='layer group' /> 模型梯队
          </Button>
        )}
        <Button onClick={() => viewHistoryReport()} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}>
          <Icon name='chart bar' /> 测试报告
        </Button>
        <Button onClick={() => setModelProbeOpen(true)} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button} color='violet'>
          <Icon name='search' /> 全渠道模型探针
        </Button>
        <Button onClick={() => setCalibrateOpen(true)} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button} color='orange'>
          <Icon name='sync alternate' /> 批量模型校准
        </Button>
        <Button onClick={refresh} loading={loading} size={isMobile ? 'tiny' : 'mini'} style={isMobile ? { borderRadius: 8, fontSize: 12, padding: '10px 8px', margin: 0, whiteSpace: 'nowrap' } : styles.button}>
          <Icon name='refresh' /> {t('channel.buttons.refresh')}
        </Button>
        </div>
      </section>

      {/* 渠道表格 */}
      <section className='studio-management-table-card studio-channel-table-card'>
        <div className='studio-management-table-card__head'>
          <div>
            <span className='studio-section-eyebrow'>CHANNEL INVENTORY</span>
            <h3>{t('channel.studio.title')}</h3>
          </div>
          <span className='studio-record-count'>{channels.length}</span>
        </div>
        <div className='studio-management-table-scroll'>
        {isMobile ? (
          /* 手机端卡片列表 */
          renderMobileChannelList()
        ) : (
        /* 桌面端表格 */
        <>
        <Table basic='very' compact size='small' className='studio-management-table studio-channel-table' style={{ margin: 0, minWidth: 1350 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortChannel('name')}>渠道</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortChannel('status')}>状态 / 调度</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortChannel('response_time')}>指标</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader} onClick={() => sortChannel('last_success_time')}>最近调用</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>模型</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>测试</Table.HeaderCell>
              <Table.HeaderCell style={styles.tableHeader}>{t('channel.table.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

        <Table.Body>
          {/* 按渠道名称分组，可展开查看组内渠道 */}
          {groupedChannels.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={colSpan} textAlign='center' style={{ padding: 60, color: '#6b7280' }}>
                  <Icon name='inbox' size='large' style={{ marginBottom: 12, display: 'block', opacity: 0.5 }} />
                  {loading ? <><Icon name='spinner' loading /> 加载中...</> : '暂无渠道数据'}
                </Table.Cell>
              </Table.Row>
            ) : (
              // 全部分组都渲染
              groupedChannels
                .map((g) => {
                // 默认折叠（undefined ?? false === false），用户点击后可切换
                const isExpanded = expandedGroups[g.key] ?? false;
                const groupModels = collectChannelModels(g.channels);
                return (
                  <React.Fragment key={g.key || '__default__'}>
                    {/* 分组标题行：可点击展开/折叠 */}
                    <Table.Row
                      style={{
                        ...styles.groupRow,
                        ...(hoveredGroup === g.key ? styles.groupRowHover : {}),
                      }}
                      onMouseEnter={() => setHoveredGroup(g.key)}
                      onMouseLeave={() => setHoveredGroup(null)}
                    >
                      <Table.Cell colSpan={colSpan} style={{ padding: '12px 16px' }}>
                        <div className='studio-channel-group-header'>
                          <button
                            type='button'
                            className='studio-channel-group-toggle'
                            aria-expanded={isExpanded}
                            aria-controls={`channel-group-${encodeURIComponent(g.key)}`}
                            onClick={() => toggleGroup(g.key)}
                          >
                          <Icon
                            name={isExpanded ? 'caret down' : 'caret right'}
                            style={{ color: '#6b7280', fontSize: 16 }}
                          />
                          <span style={{ fontWeight: 600, color: '#18181b', fontSize: 14 }}>{g.name}</span>
                          <span style={{
                            padding: '2px 10px',
                            borderRadius: 6,
                            background: 'rgba(0,0,0,0.08)',
                            color: '#6b7280',
                            fontSize: 12,
                            fontWeight: 500,
                          }}>
                            共 {g.channels.length} 个
                          </span>
                          <span style={{
                            padding: '2px 10px',
                            borderRadius: 6,
                            background: g.lastSuccessTime ? '#eff6ff' : '#f5f5f5',
                            color: g.lastSuccessTime ? '#2563eb' : '#9ca3af',
                            fontSize: 12,
                            fontWeight: 500,
                          }}>
                            最近调用 {g.lastSuccessTime ? timestamp2string(g.lastSuccessTime) : '未调用'}
                          </span>
                          {/* 模型标签列表 */}
                          <div className='studio-channel-group-models'>
                            {groupModels.slice(0, 2).map((model) => <span key={model}>{model}</span>)}
                            {groupModels.length > 2 && <span>+{groupModels.length - 2}</span>}
                          </div>
                          {g.enabledCount > 0 && (
                            <span style={{
                              padding: '2px 10px',
                              borderRadius: 6,
                              background: '#f0fdf4',
                              color: '#16a34a',
                              fontSize: 12,
                              fontWeight: 500,
                              display: 'inline-block',
                            }}>
                              <Icon name='check circle' style={{ margin: 0, fontSize: 10 }} /> 启用 {g.enabledCount}
                            </span>
                          )}
                          {(g.channels.length - g.enabledCount) > 0 && (
                            <span style={{
                              padding: '2px 10px',
                              borderRadius: 6,
                              background: '#fef2f2',
                              color: '#dc2626',
                              fontSize: 12,
                              fontWeight: 500,
                            }}>
                              禁用 {g.channels.length - g.enabledCount}
                            </span>
                          )}
                          </button>
                          <div className='studio-channel-group-actions'>
                            <Button
                              size='mini'
                              basic
                              disabled={batchTestTask?.status === 'running'}
                              onClick={(e) => {
                                e.stopPropagation();
                                const channelIDs = g.channels.map((channel) => channel.id);
                                if (channelIDs.length === 0) {
                                  showError(`分组「${g.name}」没有渠道`);
                                  return;
                                }
                                if (groupModels.length === 0) {
                                  showError(`分组「${g.name}」没有模型`);
                                  return;
                                }
                                startBatchTest(g.name, channelIDs, groupModels);
                              }}
                              style={{
                                borderRadius: 8,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              }}
                            >
                              <Icon name='bolt' style={{ margin: 0 }} /> 一键测试
                              <span style={{
                                display: 'inline-block', marginLeft: 4, padding: '1px 6px',
                                borderRadius: 4, background: 'rgba(0,0,0,0.06)', color: '#18181b',
                                fontSize: 10, fontWeight: 600,
                              }}>
                                {groupModels.length} 模型
                              </span>
                            </Button>
                            <Button
                              size='mini'
                              basic
                              onClick={(e) => {
                                e.stopPropagation();
                                viewHistoryReport(g);
                              }}
                              style={{
                                borderRadius: 8,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                              }}
                            >
                              <Icon name='chart bar' style={{ margin: 0 }} /> 报告
                            </Button>
                            <Button
                              size='mini'
                              basic
                              onClick={(e) => {
                                e.stopPropagation();
                                openGroupModelModal(g.name);
                              }}
                              style={{
                                borderRadius: 8,
                                fontSize: 12,
                                whiteSpace: 'nowrap',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                              }}
                            >
                              <Icon name='cubes' style={{ margin: 0 }} /> 管理模型
                            </Button>
                            {/* 根据分组内是否存在启用的渠道动态显示一键禁用/一键启用 */}
                            {g.enabledCount > 0 ? (
                              <Popup
                                on='click'
                                pinned
                                open={batchConfirmGroup === g.name}
                                onOpen={() => setBatchConfirmGroup(g.name)}
                                onClose={() => setBatchConfirmGroup('')}
                                trigger={
                                  <Button
                                    size='mini'
                                    basic
                                    color='red'
                                    onClick={(e) => { e.stopPropagation(); setBatchConfirmGroup(prev => prev === g.name ? '' : g.name); }}
                                    style={{
                                      borderRadius: 8,
                                      fontSize: 12,
                                      whiteSpace: 'nowrap',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                    }}
                                  >
                                    <Icon name='ban' style={{ margin: 0 }} /> 一键禁用
                                  </Button>
                                }
                                content={
                                  <div style={{ padding: 8, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                                      确认禁用 [{g.name}] 下所有启用的渠道？
                                    </div>
                                    <Button size='mini' fluid negative
                                      onClick={(e) => { e.stopPropagation(); setBatchConfirmGroup(''); disableGroupChannels(g.channels, g.name); }}
                                      style={{ borderRadius: 6, fontSize: 12 }}
                                    >
                                      <Icon name='check' /> 确认禁用全部
                                    </Button>
                                  </div>
                                }
                              />
                            ) : (
                              <Popup
                                on='click'
                                pinned
                                open={batchConfirmGroup === g.name}
                                onOpen={() => setBatchConfirmGroup(g.name)}
                                onClose={() => setBatchConfirmGroup('')}
                                trigger={
                                  <Button
                                    size='mini'
                                    basic
                                    color='green'
                                    onClick={(e) => { e.stopPropagation(); setBatchConfirmGroup(prev => prev === g.name ? '' : g.name); }}
                                    style={{
                                      borderRadius: 8,
                                      fontSize: 12,
                                      whiteSpace: 'nowrap',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                    }}
                                  >
                                    <Icon name='check circle' style={{ margin: 0 }} /> 一键启用
                                  </Button>
                                }
                                content={
                                  <div style={{ padding: 8, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                                      确认启用 [{g.name}] 下所有已禁用的渠道？
                                    </div>
                                    <Button size='mini' fluid positive
                                      onClick={(e) => { e.stopPropagation(); setBatchConfirmGroup(''); enableGroupChannels(g.channels, g.name); }}
                                      style={{ borderRadius: 6, fontSize: 12 }}
                                    >
                                      <Icon name='check' /> 确认启用全部
                                    </Button>
                                  </div>
                                }
                              />
                            )}
                            {/* 一键删除：调后端批量删除接口，删除该分组下所有渠道（不可撤销） */}
                            <Popup
                              on='click'
                              pinned
                              open={batchDeleteConfirmGroup === g.name}
                              onOpen={() => setBatchDeleteConfirmGroup(g.name)}
                              onClose={() => setBatchDeleteConfirmGroup('')}
                              trigger={
                                <Button
                                  size='mini'
                                  basic
                                  color='red'
                                  onClick={(e) => { e.stopPropagation(); setBatchDeleteConfirmGroup(prev => prev === g.name ? '' : g.name); }}
                                  style={{
                                    borderRadius: 8,
                                    fontSize: 12,
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                                  }}
                                >
                                  <Icon name='trash' style={{ margin: 0 }} /> 一键删除
                                </Button>
                              }
                              content={
                                <div style={{ padding: 8, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                                  <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>
                                    ⚠ 确认删除 [{g.name}] 下所有 {g.channels.length} 个渠道？此操作不可撤销！
                                  </div>
                                  <Button size='mini' fluid negative
                                    onClick={(e) => { e.stopPropagation(); setBatchDeleteConfirmGroup(''); deleteGroupChannels(g.channels, g.name); }}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                  >
                                    <Icon name='check' /> 确认删除全部
                                  </Button>
                                </div>
                              }
                            />
                          </div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    {/* 分组下的渠道行（仅展开时显示） */}
                    {isExpanded && g.channels.map((channel, idx) => React.cloneElement(
                      renderChannelRow(channel, idx, g.name),
                      idx === 0 ? { id: `channel-group-${encodeURIComponent(g.key)}` } : undefined
                    ))}
                  </React.Fragment>
                );
              })
          )}
        </Table.Body>
      </Table>
        </>
        )}
        </div>
      </section>

      {/* 单个渠道模型管理弹窗 */}
      <Modal
        open={modelModalOpen}
        onClose={closeModelModal}
        size='small'
        closeIcon
        className='studio-modal studio-channel-modal'
        style={{
          borderRadius: 20,
          boxShadow: '0 18px 45px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,233,0.06) 55%, rgba(248,250,252,1) 100%)',
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <Icon name='cubes' style={{ color: '#18181b', fontSize: 18, margin: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#18181b' }}>
              管理模型 — {modelModalChannel?.name || `渠道 #${modelModalChannel?.id}`}
            </div>
            {modelModalChannel?.models?.length > 0 && (
              <span style={{
                display: 'inline-block', marginTop: 4, padding: '1px 8px', borderRadius: 6,
                background: 'rgba(0,0,0,0.06)', color: '#18181b', fontSize: 11, fontWeight: 600,
              }}>
                当前 {modelModalChannel.models.length} 个模型
              </span>
            )}
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: '20px 24px', background: '#ffffff' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>
                模型列表（每行一个模型，或用逗号分隔）
              </label>
              <Button
                size='mini'
                color='teal'
                loading={fetchModelsLoading}
                disabled={fetchModelsLoading}
                onClick={fetchUpstreamModels}
                style={{ borderRadius: 8, fontSize: 12, fontWeight: 600 }}
              >
                <Icon name='cloud download' /> 一键导入所有模型
              </Button>
            </div>
            <textarea
              value={modelModalModels}
              onChange={(e) => setModelModalModels(e.target.value)}
              placeholder={'gpt-3.5-turbo\ngpt-4\ngpt-4-turbo\ncode-davinci-002'}
              style={{
                width: '100%',
                minHeight: 200,
                padding: 12,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                lineHeight: 1.6,
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                background: '#f5f5f5',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#18181b';
                e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.08)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          {modelModalChannel?.models?.length > 0 && (
            <div style={{
              padding: '10px 14px',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: 10,
              border: '1px solid #bfdbfe',
              fontSize: 12,
              color: '#18181b',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              alignItems: 'center',
            }}>
              <Icon name='info circle' style={{ margin: '0 4px 0 0', flexShrink: 0 }} />
              <strong style={{ marginRight: 4, flexShrink: 0 }}>当前：</strong>
              {modelModalChannel.models.slice(0, 5).map(m => (
                <span key={m} style={{
                  display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                  background: 'rgba(0,0,0,0.06)', color: '#18181b', fontSize: 11, fontWeight: 500,
                  maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{m}</span>
              ))}
              {modelModalChannel.models.length > 5 && (
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  +{modelModalChannel.models.length - 5} 更多
                </span>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{
          background: '#f5f5f5',
          borderTop: '1px solid #e2e8f0',
          padding: '14px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}>
          <Button
            size='small'
            basic
            color='red'
            onClick={() => updateChannelModels('remove')}
            loading={modelModalLoading}
            style={{ borderRadius: 10, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          >
            <Icon name='minus' /> 移除输入的模型
          </Button>
          <Button
            size='small'
            color='green'
            onClick={() => updateChannelModels('add')}
            loading={modelModalLoading}
            style={{ borderRadius: 10, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          >
            <Icon name='plus' /> 添加输入的模型
          </Button>
          <Button
            size='small'
            primary
            onClick={() => updateChannelModels('set')}
            loading={modelModalLoading}
            style={{ borderRadius: 10, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          >
            <Icon name='save' /> 完全覆盖
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 分组批量管理模型弹窗 */}
      <Modal
        open={groupModelModalOpen}
        onClose={closeGroupModelModal}
        size='small'
        closeIcon
        className='studio-modal studio-channel-modal'
        style={{
          borderRadius: 20,
          boxShadow: '0 18px 45px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,233,0.06) 55%, rgba(248,250,252,1) 100%)',
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <Icon name='cubes' style={{ color: '#18181b', fontSize: 18 }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#18181b' }}>
            批量管理模型 — {groupModelModalGroup || '默认'}
          </span>
        </Modal.Header>
        <Modal.Content style={{ padding: '20px 24px', background: '#ffffff' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 13 }}>
              操作类型
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                basic={groupModelModalAction !== 'add'}
                color={groupModelModalAction === 'add' ? 'green' : undefined}
                onClick={() => setGroupModelModalAction('add')}
                style={{ borderRadius: 10, flex: 1, fontWeight: 600, fontSize: 13 }}
              >
                <Icon name='plus' /> 批量添加
              </Button>
              <Button
                basic={groupModelModalAction !== 'remove'}
                color={groupModelModalAction === 'remove' ? 'red' : undefined}
                onClick={() => setGroupModelModalAction('remove')}
                style={{ borderRadius: 10, flex: 1, fontWeight: 600, fontSize: 13 }}
              >
                <Icon name='minus' /> 批量移除
              </Button>
              <Button
                basic={groupModelModalAction !== 'set'}
                color={groupModelModalAction === 'set' ? 'blue' : undefined}
                onClick={() => setGroupModelModalAction('set')}
                style={{ borderRadius: 10, flex: 1, fontWeight: 600, fontSize: 13 }}
              >
                <Icon name='save' /> 完全覆盖
              </Button>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontWeight: 600, color: '#374151', fontSize: 13 }}>
                模型列表（每行一个模型，或用逗号分隔）
              </label>
              <Button
                size='mini'
                color='teal'
                loading={fetchModelsLoading}
                disabled={fetchModelsLoading}
                onClick={fetchGroupUpstreamModels}
                style={{ borderRadius: 8, fontSize: 12, fontWeight: 600 }}
              >
                <Icon name='cloud download' /> 一键导入所有模型
              </Button>
            </div>
            <textarea
              value={groupModelModalModels}
              onChange={(e) => setGroupModelModalModels(e.target.value)}
              placeholder={'gpt-3.5-turbo\ngpt-4\ngpt-4-turbo'}
              style={{
                width: '100%',
                minHeight: 140,
                padding: 12,
                borderRadius: 10,
                border: '1px solid #e2e8f0',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                lineHeight: 1.6,
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                background: '#f5f5f5',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#18181b'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(0,0,0,0.08)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div style={{
            padding: '12px 16px',
            background: groupModelModalAction === 'set' ? 'rgba(0,0,0,0.04)' : groupModelModalAction === 'add' ? '#f0fdf4' : '#fef2f2',
            borderRadius: 10,
            border: `1px solid ${groupModelModalAction === 'set' ? '#bfdbfe' : groupModelModalAction === 'add' ? 'rgba(22,163,74,0.3)' : 'rgba(220,38,38,0.3)'}`,
            fontSize: 13,
            color: groupModelModalAction === 'set' ? '#18181b' : groupModelModalAction === 'add' ? '#16a34a' : '#dc2626',
          }}>
            <Icon name='exclamation triangle' style={{ margin: '0 6px 0 0' }} />
            {groupModelModalAction === 'set'
              ? '将用当前输入的模型完全替换该分组下所有渠道的模型列表'
              : groupModelModalAction === 'add'
              ? '将为该分组下的所有渠道添加输入的模型（已存在的模型不会重复添加）'
              : '将从该分组下的所有渠道中移除输入的模型'}
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: '#f5f5f5',
          borderTop: '1px solid #e2e8f0',
          padding: '14px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}>
          <Button size='small' basic onClick={closeGroupModelModal} style={{ borderRadius: 10, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>取消</Button>
          <Button
            size='small'
            color={groupModelModalAction === 'set' ? 'blue' : groupModelModalAction === 'add' ? 'green' : 'red'}
            onClick={updateGroupModels}
            loading={groupModelModalLoading}
            style={{ borderRadius: 10, fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          >
            <Icon name={groupModelModalAction === 'set' ? 'save' : groupModelModalAction === 'add' ? 'plus' : 'minus'} />
            {groupModelModalAction === 'set' ? '完全覆盖' : groupModelModalAction === 'add' ? '批量添加' : '批量移除'}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 全渠道模型探针弹窗 */}
      <Modal
        open={modelProbeOpen}
        onClose={() => setModelProbeOpen(false)}
        size='large'
        closeIcon
        className='studio-modal studio-channel-modal'
        style={{ borderRadius: 20 }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          padding: '18px 24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name='search' style={{ color: '#18181b', fontSize: 20, margin: 0 }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#18181b' }}>全渠道模型探针</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>按同名渠道组抽取一个代表渠道，拉取模型并执行文本、图片、工具探测</div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Content style={{ padding: '20px 24px', background: '#ffffff' }}>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>需要探测的模型 ID</label>
                <Input
                  value={modelProbeModel}
                  onChange={(e) => setModelProbeModel(e.target.value)}
                  placeholder='gpt-4o-mini'
                  disabled={modelProbeTask?.status === 'running'}
                />
              </Form.Field>
              <Form.Field>
                <label>并发数</label>
                <Input
                  type='number'
                  min='1'
                  max='10'
                  value={modelProbeConcurrency}
                  onChange={(e) => setModelProbeConcurrency(e.target.value)}
                  disabled={modelProbeTask?.status === 'running'}
                />
              </Form.Field>
              <Form.Field>
                <label>探测范围</label>
                <Form.Select
                  value={modelProbeScope}
                  onChange={(e, { value }) => setModelProbeScope(value)}
                  disabled={modelProbeTask?.status === 'running'}
                  options={[
                    { key: 'all', text: '全部渠道', value: 'all' },
                    { key: 'enabled', text: '仅启用渠道', value: 'enabled' },
                  ]}
                />
              </Form.Field>
            </Form.Group>
            <Button
              primary
              icon='play'
              content='开始探测'
              onClick={startModelProbe}
              loading={modelProbeTask?.status === 'running'}
              disabled={modelProbeTask?.status === 'running'}
              style={styles.button}
            />
          </Form>

          {modelProbeTask && (
            <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, color: '#18181b' }}>
                  {modelProbeTask.status === 'running' ? '探测中' : '探测完成'}：{modelProbeTask.model}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Label size='mini' basic>进度 {modelProbeTask.completed}/{modelProbeTask.total}</Label>
                  <Label size='mini' basic color='green'>命中 {modelProbeTask.matched}</Label>
                  <Label size='mini' basic color='red'>失败 {modelProbeTask.failed}</Label>
                </div>
              </div>
              <Progress
                percent={Math.round(modelProbeTask.percentage || 0)}
                indicating={modelProbeTask.status === 'running'}
                success={modelProbeTask.status === 'completed'}
                error={modelProbeTask.status === 'failed'}
                progress
                size='small'
                style={{ margin: 0 }}
              />
              {modelProbeTask.error_message && (
                <Message negative size='small' style={{ marginTop: 10 }}>{modelProbeTask.error_message}</Message>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, overflowX: 'auto' }}>
            <Table compact='very' celled selectable style={{ margin: 0, minWidth: 860 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell style={styles.tableHeader}>同名渠道组</Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader}>代表渠道</Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader} onClick={() => changeModelProbeSort('response_time_ms')}>综合响应 <Icon name='sort' /></Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader} onClick={() => changeModelProbeSort('text')}>文本 <Icon name='sort' /></Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader} onClick={() => changeModelProbeSort('vision')}>图片 <Icon name='sort' /></Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader} onClick={() => changeModelProbeSort('tool')}>工具 <Icon name='sort' /></Table.HeaderCell>
                  <Table.HeaderCell style={styles.tableHeader}>状态</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {modelProbeReports.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan='7' textAlign='center' style={{ padding: 36, color: '#6b7280' }}>
                      {modelProbeTask?.status === 'running' ? <><Icon name='spinner' loading /> 正在生成报告...</> : '暂无探针报告'}
                    </Table.Cell>
                  </Table.Row>
                ) : modelProbeReports.map((report) => (
                  <Table.Row key={`${report.group_name}-${report.channel_id}-${report.model}`}>
                    <Table.Cell>{report.group_name}</Table.Cell>
                    <Table.Cell>#{report.channel_id} {report.channel_name || '未命名'}</Table.Cell>
                    <Table.Cell>{formatProbeTime(report.response_time_ms)}</Table.Cell>
                    <Table.Cell>{renderProbeCapability(report.text)}</Table.Cell>
                    <Table.Cell>{renderProbeCapability(report.vision)}</Table.Cell>
                    <Table.Cell>{renderProbeCapability(report.tool)}</Table.Cell>
                    <Table.Cell>
                      {report.error ? <Label size='mini' basic color='red'>{report.error}</Label> : <Label size='mini' basic color='green'>已命中</Label>}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Modal.Content>
        <Modal.Actions style={{ background: '#f5f5f5', borderTop: '1px solid rgba(0,0,0,0.08)', padding: '14px 24px' }}>
          <Button onClick={() => setModelProbeOpen(false)} style={styles.button}>关闭</Button>
        </Modal.Actions>
      </Modal>

      {/* 批量模型校准弹窗 */}
      <Modal
        open={calibrateOpen}
        onClose={() => { setCalibrateOpen(false); setCalibrateResult(null); setCalibrateSelected(new Set()); setCalibrateSearch(''); }}
        size={isMobile ? 'fullscreen' : 'large'}
        className='model-calibrate-modal'
        style={isMobile ? { width: '95vw', top: '2vh' } : undefined}
      >
        <Modal.Header className='model-calibrate-header'>
          <span className='model-calibrate-header__icon'><Icon name='sync alternate' /></span>
          <div><strong>批量模型校准</strong><p>拉取上游真实模型，筛选后统一应用到渠道</p></div>
        </Modal.Header>
        <Modal.Content className='model-calibrate-content' style={{ maxHeight: isMobile ? '75vh' : '70vh', overflowY: 'auto' }}>
          {calibrateLoading ? (
            /* 拉取中：进度条 */
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ marginBottom: 16 }}>
                <Icon name='sync alternate' size='huge' style={{ color: '#4f63a8', animation: 'spin 2s linear infinite' }} />
              </div>
              <h3 style={{ color: '#18181b', marginBottom: 16 }}>正在拉取上游模型...</h3>
              <Progress
                percent={calibrateProgress}
                indicating
                size='small'
                style={{ maxWidth: 400, margin: '0 auto 8px' }}
              />
              <p style={{ fontSize: 12, color: '#9ca3af' }}>{calibrateProgressText}</p>
            </div>
          ) : !calibrateResult ? (
            /* 第一步：选范围 + 拉取 */
            <div className='model-calibrate-welcome'>
              <div className='model-calibrate-steps'>
                <span className='is-active'><b>1</b> 拉取模型</span><i></i><span><b>2</b> 筛选模型</span><i></i><span><b>3</b> 应用校准</span>
              </div>
              <div className='model-calibrate-welcome__icon'><Icon name='cloud download' /></div>
              <h3>从真实上游同步模型能力</h3>
              <p>系统会按站点去重并并发拉取，不会在本步骤修改任何渠道。拉取完成后由你选择要保留的模型。</p>
              <div className='model-calibrate-scope-card'>
                <div><strong>校准范围</strong><span>决定本次要检查哪些渠道</span></div>
                <Form.Select
                  value={calibrateScope}
                  onChange={(e, { value }) => setCalibrateScope(value)}
                  options={[
                    { key: 'all', text: '全部渠道', value: 'all' },
                    { key: 'enabled', text: '仅启用渠道', value: 'enabled' },
                  ]}
                />
              </div>
              <Button primary size='large' onClick={calibratePreview} icon='sync alternate' content='开始拉取模型' className='model-calibrate-primary' />
            </div>
          ) : (
            /* 第二步：展示模型列表，搜索+勾选 */
          <div className='model-calibrate-result'>
              <div className='model-calibrate-steps'>
                <span className='is-done'><b><Icon name='check' /></b> 已拉取</span><i className='is-done'></i><span className='is-active'><b>2</b> 筛选模型</span><i></i><span><b>3</b> 应用校准</span>
              </div>
              {/* 顶部统计 + 搜索 */}
              <div className='model-calibrate-stats'>
                <Label size='medium' basic>共 {calibrateResult.all_models.length} 个模型</Label>
                <Label size='medium' basic>{calibrateResult.total} 个渠道</Label>
                <Label size='medium' color='blue'>已选 {calibrateSelected.size}</Label>
                {calibrateResult.failed > 0 && <Label size='medium' color='red'>失败 {calibrateResult.failed}</Label>}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <Input
                  icon='search'
                  placeholder='搜索模型名称...'
                  value={calibrateSearch}
                  onChange={(e) => setCalibrateSearch(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button size='mini' basic onClick={() => {
                  const all = new Set(calibrateResult.all_models);
                  setCalibrateSelected(all);
                  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); localStorage.setItem(`calibrate_selected_models_${u.id||'default'}`, JSON.stringify(calibrateResult.all_models)); } catch (e) {}
                }}>全选</Button>
                <Button size='mini' basic onClick={() => { setCalibrateSelected(new Set()); try { const u = JSON.parse(localStorage.getItem('user') || '{}'); localStorage.setItem(`calibrate_selected_models_${u.id||'default'}`, '[]'); } catch (e) {} }}>清空</Button>
              </div>

              {/* 选中模型列表 */}
              {calibrateSelected.size > 0 && (
                <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
                    已选模型 ({calibrateSelected.size})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 80, overflowY: 'auto' }}>
                    {Array.from(calibrateSelected).map(m => {
                      const ch = Array.from(new Set(calibrateResult.model_channels[m] || [])).length;
                      return (
                        <span key={m} onClick={() => toggleCalibrateModel(m)} style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: 11,
                          background: '#dbeafe', color: '#1e40af', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                          {m} <span style={{ color: '#3b82f6', fontSize: 10 }}>({ch}渠道)</span>
                          <Icon name='times' style={{ margin: 0, fontSize: 10 }} />
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 模型列表 - 可勾选 */}
              <div style={{ maxHeight: isMobile ? '35vh' : '40vh', overflowY: 'auto', borderRadius: 10, border: '1px solid rgba(0,0,0,0.06)', background: '#fff' }}>
                {calibrateResult.all_models
                  .filter(m => !calibrateSearch || m.toLowerCase().includes(calibrateSearch.toLowerCase()))
                  .map(model => {
                    const isSelected = calibrateSelected.has(model);
                    const rawChannels = calibrateResult.model_channels[model] || [];
                    const channels = Array.from(new Set(rawChannels)); // 去重
                    return (
                      <div
                        key={model}
                        onClick={() => toggleCalibrateModel(model)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', cursor: 'pointer',
                          borderBottom: '1px solid rgba(0,0,0,0.04)',
                          background: isSelected ? '#eff6ff' : '#fff',
                          transition: 'background 0.1s',
                        }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                          border: isSelected ? '2px solid #2563eb' : '2px solid #d1d5db',
                          background: isSelected ? '#2563eb' : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {isSelected && <Icon name='check' style={{ color: '#fff', fontSize: 12, margin: 0 }} />}
                        </div>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#18181b', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {model}
                        </span>
                        <span style={{ fontSize: 10, color: channels.length > 0 ? '#16a34a' : '#dc2626', flexShrink: 0, fontWeight: 600 }}>
                          {channels.length > 0 ? `${channels.length}个渠道` : '无渠道'}
                        </span>
                      </div>
                    );
                  })
                }
              </div>

              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>
                💡 勾选模型后点击「应用」，每个渠道只保留选中的模型，不在列表中的模型将被抛弃。无匹配的渠道模型将被清空并禁用。选择会自动保存，下次打开时恢复。
              </p>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{ background: '#f5f5f5', borderTop: '1px solid rgba(0,0,0,0.08)', padding: '14px 24px' }}>
          {calibrateResult ? (
            <>
              <Button onClick={() => setCalibrateResult(null)} style={styles.button}>返回</Button>
              <Button negative loading={calibrateApplying} onClick={calibrateApply} icon='check' content={`应用校准（选中 ${calibrateSelected.size} 个模型）`} />
            </>
          ) : (
            <Button onClick={() => setCalibrateOpen(false)}>取消</Button>
          )}
        </Modal.Actions>
      </Modal>

      {/* 一键导入站点配置弹窗 */}
      <ImportSiteModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportSuccess={refresh}
      />

      {isRoot() && (
        <ModelChannelPriorityModal
          open={modelPriorityModalOpen}
          onClose={() => setModelPriorityModalOpen(false)}
        />
      )}

      {isRoot() && (
        <ModelTierModal
          open={modelTierModalOpen}
          onClose={() => setModelTierModalOpen(false)}
        />
      )}

      {/* 测试报告弹窗 */}
      <Modal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        size='large'
        closeIcon
        className='studio-modal studio-channel-modal'
        style={{ borderRadius: 20 }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0fdf4 100%)',
          borderBottom: '1px solid #bae6fd',
          padding: '18px 24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon name='chart bar' style={{ color: '#0284c7', fontSize: 22, margin: 0 }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#18181b' }}>模型测试报告</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{reportTitle}</div>
            </div>
          </div>
          {reportData.length > 0 && (
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              {[
                { label: '总计', value: reportData.length, color: '#6b7280', bg: '#f5f5f5' },
                { label: '成功', value: reportData.filter(r => r.success).length, color: '#16a34a', bg: '#f0fdf4' },
                { label: '失败', value: reportData.filter(r => !r.success).length, color: '#dc2626', bg: '#fef2f2' },
                { label: '平均响应', value: (() => {
                  const times = reportData.filter(r => r.success).map(r => r.response_time);
                  if (times.length === 0) return '-';
                  const avg = times.reduce((a, b) => a + b, 0) / times.length;
                  return avg < 1000 ? `${Math.round(avg)}ms` : `${(avg / 1000).toFixed(2)}s`;
                })(), color: '#0284c7', bg: '#f0f9ff' },
              ].map(stat => (
                <div key={stat.label} style={{
                  padding: '6px 14px', borderRadius: 10, background: stat.bg,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 70,
                }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: stat.color }}>{stat.value}</span>
                  <span style={{ fontSize: 10, color: stat.color, fontWeight: 500 }}>{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </Modal.Header>
        <Modal.Content style={{ padding: '16px 24px', maxHeight: 480, overflowY: 'auto' }}>
          {reportData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <Icon name='inbox' size='large' style={{ marginBottom: 12, display: 'block', opacity: 0.5 }} />
              暂无测试报告数据
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[
                  { key: 'all', label: '全部' },
                  { key: 'success', label: '成功' },
                  { key: 'failed', label: '失败' },
                ].map(f => (
                  <Button key={f.key} size='mini'
                    active={reportFilter === f.key}
                    onClick={() => setReportFilter(f.key)}
                    style={{ borderRadius: 8, fontSize: 12 }}
                  >
                    {f.label}
                    <span style={{
                      marginLeft: 4, padding: '1px 5px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: reportFilter === f.key ? 'rgba(0,0,0,0.06)' : '#f5f5f5',
                    }}>
                      {f.key === 'all' ? reportData.length :
                       f.key === 'success' ? reportData.filter(r => r.success).length :
                       reportData.filter(r => !r.success).length}
                    </span>
                  </Button>
                ))}
              </div>
              <Table compact size='small' style={{ borderRadius: 12, overflow: 'hidden' }}>
                <Table.Header>
                  <Table.Row style={{ background: '#f5f5f5' }}>
                    {[
                      { field: 'model_name', label: '模型名称' },
                      { field: 'channel_name', label: '渠道' },
                      { field: 'success', label: '状态' },
                      { field: 'response_time', label: '响应时间' },
                      { field: '', label: '能力测试 (文本|图片|工具)' },
                    ].map(col => (
                      <Table.HeaderCell
                        key={col.label}
                        style={{
                          fontSize: 11, fontWeight: 700, color: '#6b7280',
                          cursor: col.field ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={() => {
                          if (!col.field) return;
                          setReportSort(prev => ({
                            field: col.field,
                            direction: prev.field === col.field && prev.direction === 'asc' ? 'desc' : 'asc',
                          }));
                        }}
                      >
                        {col.label}
                        {col.field && (
                          <Icon
                            name={reportSort.field === col.field
                              ? (reportSort.direction === 'asc' ? 'sort ascending' : 'sort descending')
                              : 'sort'}
                            style={{
                              marginLeft: 4, fontSize: 10, opacity: reportSort.field === col.field ? 1 : 0.4,
                              color: reportSort.field === col.field ? '#18181b' : '#6b7280',
                            }}
                          />
                        )}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(() => {
                    let rows = reportData.filter(r => reportFilter === 'all' || (reportFilter === 'success' ? r.success : !r.success));
                    if (reportSort.field) {
                      const dir = reportSort.direction === 'asc' ? 1 : -1;
                      rows = [...rows].sort((a, b) => {
                        const va = a[reportSort.field];
                        const vb = b[reportSort.field];
                        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
                        if (typeof va === 'boolean' && typeof vb === 'boolean') return (va === vb ? 0 : va ? -1 : 1) * dir;
                        const sa = String(va || '');
                        const sb = String(vb || '');
                        return sa.localeCompare(sb) * dir;
                      });
                    }
                    return rows.map((r, i) => {
                      const rt = r.response_time;
                      const rtColor = rt < 1000 ? '#16a34a' : rt < 3000 ? '#d97706' : '#dc2626';
                      const rtDisplay = rt < 1000 ? `${rt}ms` : `${(rt / 1000).toFixed(2)}s`;
                      const testedAt = r.tested_at ? new Date(r.tested_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-';
                      return (
                        <Table.Row key={i} style={{
                          background: r.success ? '#fafafa' : '#fef2f2',
                          transition: 'background 0.15s',
                        }}>
                          <Table.Cell>
                            <span
                              title='点击复制'
                              onClick={() => {
                                navigator.clipboard.writeText(r.model_name).then(() => showSuccess(`已复制: ${r.model_name}`));
                              }}
                              style={{
                                fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                borderBottom: '1px dashed #93c5fd', color: '#18181b',
                                paddingBottom: 1,
                              }}
                            >
                              {r.model_name}
                            </span>
                          </Table.Cell>
                          <Table.Cell style={{ fontSize: 12, color: '#6b7280' }}>{r.channel_name}</Table.Cell>
                          <Table.Cell>
                            {r.success ? (
                              <Label size='mini' color='green' basic>
                                <Icon name='check' style={{ margin: 0 }} /> 通过
                              </Label>
                            ) : (
                              <Label size='mini' color='red' basic>
                                <Icon name='close' style={{ margin: 0 }} /> 失败
                              </Label>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            <span style={{ fontSize: 12, fontWeight: 600, color: rtColor, fontVariantNumeric: 'tabular-nums' }}>
                              {rtDisplay}
                            </span>
                          </Table.Cell>
                          <Table.Cell style={{ fontSize: 11, color: '#18181b', maxWidth: 260 }}>
                            {renderCapabilityResult(r.error_message || '')}
                          </Table.Cell>
                        </Table.Row>
                      );
                    });
                  })()}
                </Table.Body>
              </Table>
            </>
          )}
        </Modal.Content>
        <Modal.Actions style={{
          background: '#f5f5f5',
          borderTop: '1px solid #e2e8f0',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <Button onClick={() => setReportModalOpen(false)} style={{ borderRadius: 10, fontWeight: 600 }}>
            关闭
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 删除确认弹窗 */}
      <Modal
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        size='mini'
        closeIcon
        className='studio-modal studio-channel-modal studio-channel-modal--danger'
        style={{ borderRadius: 16 }}
      >
        <Modal.Header style={{
          background: 'linear-gradient(135deg, #fee2e2 0%, rgba(220,38,38,0.3) 100%)',
          borderBottom: '1px solid #dc2626',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <Icon name='exclamation triangle' style={{ color: '#dc2626', fontSize: 20 }} />
          <span style={{ fontSize: 16, fontWeight: 600, color: '#dc2626' }}>
            确认删除渠道
          </span>
        </Modal.Header>
        <Modal.Content style={{ padding: '24px' }}>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            <p style={{ marginBottom: 16 }}>
              您确定要删除渠道 <strong style={{ color: '#dc2626' }}>{channelToDelete?.channel?.name}</strong> 吗？
            </p>
            <div style={{
              padding: 12,
              background: '#fef2f2',
              borderRadius: 10,
              border: '1px solid rgba(220,38,38,0.3)',
              fontSize: 13,
              color: '#dc2626'
            }}>
              <Icon name='warning sign' style={{ margin: '0 6px 0 0' }} />
              此操作不可撤销，删除后无法恢复
            </div>
          </div>
        </Modal.Content>
        <Modal.Actions style={{
          background: '#f5f5f5',
          borderTop: '1px solid #e2e8f0',
          padding: '14px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}>
          <Button
            onClick={closeDeleteConfirm}
            style={{ borderRadius: 10, fontWeight: 600 }}
          >
            取消
          </Button>
          <Button
            negative
            onClick={confirmDelete}
            style={{ borderRadius: 10, fontWeight: 600 }}
          >
            <Icon name='trash' /> 确认删除
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ChannelsTable;
